import React, { useState, useEffect, useRef } from 'react';
import { getSubmissionStatusColor, getSubmissionStatusLabel, getOutcomeConfig, getOutcomeFlagLabel } from '../../utils/statusBadge';
import OutcomeDrawer from '../phlebotomist/OutcomeDrawer';
import MergeSubmissionsModal from './MergeSubmissionsModal';
import MergeCandidatePicker from './MergeCandidatePicker';
import TagEditor from '../shared/TagEditor';
import { TEAM_TAGS } from '../../utils/submissionTags';
import { formatSchedule } from '../../utils/schedule';
import { useUnsavedChanges } from '../../utils/useUnsavedChanges';
import { lookupTubesForTests, tubeGuideSig } from '../../utils/labaideTube';
import TubeGuide from '../shared/TubeGuide';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import {
  Box,
  Spinner,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Progress,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  Select,
  IconButton,
  Link,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Flex,
  Divider,
  Collapse,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { EditIcon, ArrowBackIcon, AttachmentIcon, CloseIcon, AddIcon } from '@chakra-ui/icons';
import { supabase } from '../../supabaseClient';
import FedExLabelGenerator from '../shared/FedExLabelGenerator';

import { getAuthHeaders } from '../../utils/apiAuth';
import DialerrActions from './DialerrActions';
function isImageFile(url) {
  return url && /\.(jpe?g|png|gif|bmp|webp|svg)$/i.test(url);
}

const PdfIcon = () => (
  <span style={{ display: 'flex', width: 60, height: 60, background: '#f5f5f5', borderRadius: 6, border: '1px solid #ccc', alignItems: 'center', justifyContent: 'center', fontSize: 32, color: '#e53e3e' }}>
    📄
  </span>
);

// Reusable: parse image field (handles JSON strings, arrays, plain strings)
function parseImageUrls(value) {
  let imgs = value;
  if (typeof imgs === 'string') { try { imgs = JSON.parse(imgs); } catch { /* keep as string */ } }
  return (Array.isArray(imgs) ? imgs.filter(Boolean) : (imgs ? [imgs] : []));
}

function SubmissionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [submission, setSubmission] = useState(null);
  // Phlebotomist roster for the inline assign search in the teal edit panel.
  // Fetched once on mount so the picker is instant when admin enters edit mode.
  // Filtering happens client-side via phlebSearch.
  const [phlebList, setPhlebList] = useState([]);
  const [phlebSearch, setPhlebSearch] = useState('');
  const [showPhlebDropdown, setShowPhlebDropdown] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [statusDrawerOpen, setStatusDrawerOpen] = useState(false);
  // Audit log of every disposition logged on this submission.
  const [statusEvents, setStatusEvents] = useState([]);
  const [statusEventsLoading, setStatusEventsLoading] = useState(false);

  // Who's logged into the admin portal — used to stamp logged_by on
  // outcome events so the activity timeline shows the actual admin name
  // instead of a generic "Admin" badge.
  const { adminUser } = useOutletContext() || {};

  // Lookup of admin user id → friendly name so the timeline can resolve
  // historical events that only carry a uuid in their logged_by column.
  const [adminUserById, setAdminUserById] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('admin_users')
        .select('id, name, email');
      if (cancelled || error) return;
      const map = {};
      (data || []).forEach((u) => { map[u.id] = u.name || u.email || null; });
      setAdminUserById(map);
    })();
    return () => { cancelled = true; };
  }, []);
  // Resolve the actor label for a timeline event. Resolution order:
  //   1. logged_by_name (stamped at write time, added in add_logged_by_name.sql)
  //   2. lookup logged_by uuid in admin_users for events written before that column existed
  //   3. fall back to the generic kind label
  // Using the column directly means we don't need admin_users to be
  // queryable from the client, and the name survives even if the admin
  // row is later renamed or removed.
  const resolveActorLabel = (ev) => {
    if (!ev) return null;
    if (ev.logged_by_name) return ev.logged_by_name;
    if (ev.logged_by_kind === 'admin') {
      const name = ev.logged_by && adminUserById[ev.logged_by];
      return name || 'Admin';
    }
    if (ev.logged_by_kind === 'lab') return 'Lab';
    return 'Phleb';
  };
  const [mergePair, setMergePair] = useState(null); // { primary, duplicate } for the merge modal
  const [mergePickerOpen, setMergePickerOpen] = useState(false); // duplicate-candidate picker

  // Open the candidate picker — it surfaces likely duplicates (name/DOB/MRN)
  // with their date + status so the admin can choose which one to merge.
  const startMerge = () => {
    if (!submission?.id) return;
    setMergePickerOpen(true);
  };

  // The picker hands back the chosen duplicate row; queue up the confirm modal.
  const onPickMergeDuplicate = (duplicateRow) => {
    setMergePickerOpen(false);
    setMergePair({ primary: submission, duplicate: duplicateRow });
  };
  // Edit steps (tabs) default-expanded so existing admin workflow is
  // unchanged. Toggle below the history panel collapses them when admin just
  // wants to review the patient-contact trail.
  const [editStepsOpen, setEditStepsOpen] = useState(true);
  // Past-events list inside the Status card is collapsed by default — the
  // most recent update gets prime real estate; click to expand history.
  const [historyExpanded, setHistoryExpanded] = useState(false);
  // Ticks every 30s so the "last updated X ago" / "due in Y" copy in the
  // Status card stays current without a full re-render of everything else.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setNowTick((n) => n + 1), 30 * 1000);
    return () => clearInterval(i);
  }, []);

  // ── Phleb roster — for the inline assign dropdown ─────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('phlebotomist_profiles')
        .select('id, full_name, company_name, email, lab_draw_fee')
        .order('full_name', { ascending: true });
      if (cancelled) return;
      if (!error) setPhlebList(data || []);
    })();
    return () => { cancelled = true; };
  }, []);

  // Display name for the assigned phleb. Prefer the denormalized
  // submission.phlebotomist_name, but fall back to resolving phlebotomist_id
  // against the roster — several assign paths (admin list, phleb self-claim,
  // route reassign) set the id without persisting the name, which otherwise
  // shows "Unknown" even though the draw IS assigned. Returns null when there's
  // no assignment or the id no longer matches a phleb.
  const resolvePhlebName = (sub) => {
    if (!sub) return null;
    if (sub.phlebotomist_name) return sub.phlebotomist_name;
    if (sub.phlebotomist_id) {
      const p = phlebList.find((x) => x.id === sub.phlebotomist_id);
      if (p) return p.full_name || p.company_name || p.email || null;
    }
    return null;
  };

  // When admin picks a phleb in the editor, also snapshot the name and email
  // into the edit form so the displayed values update immediately and so the
  // existing save handler writes a consistent triple.
  const setEditPhleb = (newId) => {
    const target = newId || '';
    const picked = target ? phlebList.find((p) => p.id === target) : null;
    setEditForm((f) => ({
      ...f,
      phlebotomist_id: target,
      phlebotomist_name: picked ? (picked.full_name || picked.company_name || picked.email || '') : '',
      phlebotomist_email: picked?.email || '',
    }));
  };

  // Admin manual lab override state
  const [manualLabSaving, setManualLabSaving] = useState(false);
  const [unassignLoading, setUnassignLoading] = useState(false);
  // Admin lab search by name
  const [labSearchQuery, setLabSearchQuery] = useState('');
  const [labSearchResults, setLabSearchResults] = useState([]);
  const [labSearchLoading, setLabSearchLoading] = useState(false);
  const [showLabDropdown, setShowLabDropdown] = useState(false);
  // Selected lab details
  const [selectedLabDetails, setSelectedLabDetails] = useState(null);
  const [selectedLabLoading, setSelectedLabLoading] = useState(false);
  // Manually added candidate labs (persist alongside fetched list)
  const [manualAddedLabs, setManualAddedLabs] = useState([]);
  // Temporary highlight for labs added/focused
  const [highlightLabId, setHighlightLabId] = useState(null);

  // Initialize manualAddedLabs from submission (persisted in DB)
  useEffect(() => {
    if (submission && Array.isArray(submission.manual_lab_options)) {
      setManualAddedLabs(submission.manual_lab_options);
    }
  }, [submission?.manual_lab_options]);

  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  // Warn user about unsaved changes when editing
  useUnsavedChanges(isEditing);
  const [editForm, setEditForm] = useState({});
  const [doctors, setDoctors] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [isNewDoctor, setIsNewDoctor] = useState(false);
  const [doctorSearchQuery, setDoctorSearchQuery] = useState('');
  const [showDoctorDropdown, setShowDoctorDropdown] = useState(false);
  const doctorSearchRef = useRef(null);
  const [newDoctorForm, setNewDoctorForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    clinic_name: '',
    address: ''
  });
  const [insurances, setInsurances] = useState([]);
  const [insurancesLoading, setInsurancesLoading] = useState(false);
  const [insuranceSearchQuery, setInsuranceSearchQuery] = useState('');
  const [showInsuranceDropdown, setShowInsuranceDropdown] = useState(false);
  const insuranceSearchRef = useRef(null);
  const [isNewInsurance, setIsNewInsurance] = useState(false);
  const [newInsuranceForm, setNewInsuranceForm] = useState({
    company_name: '',
    provider_type: '',
    policy_number: ''
  });
  const [tests, setTests] = useState([]);
  const [testsLoading, setTestsLoading] = useState(false);
  const [testSearchQuery, setTestSearchQuery] = useState('');
  const [showTestDropdown, setShowTestDropdown] = useState(false);
  const [selectedTests, setSelectedTests] = useState([]);
  const [diagnosisCodesConfirmed, setDiagnosisCodesConfirmed] = useState({});
  // LabAide tube guide per test (keyed by test id) for the read-only Test Tube Guide box.
  const [tubeGuide, setTubeGuide] = useState(null); // single combined LabAide result
  const [availableLabs, setAvailableLabs] = useState([]);
  const [availableLabsLoading, setAvailableLabsLoading] = useState(false);
  const [fallbackPatientName, setFallbackPatientName] = useState('');
  const [patientIdNumber, setPatientIdNumber] = useState('');
  // Patients search state
  const [patients, setPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [hasPatientsEmail, setHasPatientsEmail] = useState(true);
  const [hasPatientsAddress, setHasPatientsAddress] = useState(true);
  const [hasPatientsDob, setHasPatientsDob] = useState(true);
  
  // Self-pay payment link
  const [paymentLinkLoading, setPaymentLinkLoading] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);

  // FedEx modal state
  const [fedexModalOpen, setFedexModalOpen] = useState(false);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [sendingSelection, setSendingSelection] = useState(false);
  const [selectionSent, setSelectionSent] = useState(false);
  // Manual FedEx label provision
  const [manualLabelUrl, setManualLabelUrl] = useState('');
  const [manualLabelFile, setManualLabelFile] = useState(null);
  const [manualTrackingNumber, setManualTrackingNumber] = useState('');
  const [manualLabelSaving, setManualLabelSaving] = useState(false);
  const [labResultsUploading, setLabResultsUploading] = useState(false);

  // Document upload state
  const [docUploading, setDocUploading] = useState({ script: false, insurance: false, patientId: false });
  const scriptFileRef = useRef(null);
  const insuranceCardFileRef = useRef(null);
  const patientIdFileRef = useRef(null);

  // Specimen editing state
  const specimenTypes = [
    'Blood - General', 'BLOOD SMEAR', 'Blue Top tube', 'Ear Swab',
    'Endocervical swab', 'Eye Swab', 'Fecal Smear', 'Fecal Swab', 'Fluid',
    'Gray Top tube - Sodium Flouride', 'GREEN-TOP TUBE', 'HAIR', 'iFOBT Stool',
    'Lavender Top Tube', 'Lesion swab', 'Light Blue Tube', 'MICROGRAPH', 'MISC',
    'Nail Clippings', 'NAILS', 'Nasal (mid-turbinate) swab', 'Nasal Swab (Default)',
    'Nasopharyngeal swab', 'Oral Fluid Toxicology', 'Oral Swab', 'Oropharyngeal swab',
    'Para-Pak', 'PCR', 'Pink Top tube', 'Plain Red Top Tube', 'Plasma',
    'Royal Blue Top Tube', 'Serum', 'Serum Separator tube', 'SPUTUM', 'Stool,Fresh',
    'Swab', 'Synovial fluid', 'Urea breath bags', 'Urethral swab', 'Urine Cup',
    'Urine in Gray top tube', 'Urine Molecular', 'Urine Toxicology', 'Vaginal swab',
    'Whole Blood', 'Wound', 'Yellow Top Tube - ACD A or B',
  ];
  const [specimenRows, setSpecimenRows] = useState([{ quantity: 1, type: '' }]);
  const [specimenSearches, setSpecimenSearches] = useState({});
  const [showSpecimenDropdown, setShowSpecimenDropdown] = useState(null);
  const specimenDropdownRef = useRef(null);

  // Parse specimen_type JSON into rows when submission loads
  useEffect(() => {
    if (submission && submission.specimen_type) {
      try {
        const parsed = JSON.parse(submission.specimen_type);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setSpecimenRows(parsed.map(s => ({ quantity: s.quantity || 1, type: s.type || '' })));
        }
      } catch {
        // If not JSON, treat as plain text - put in a single row
        setSpecimenRows([{ quantity: 1, type: submission.specimen_type }]);
      }
    }
  }, [submission?.specimen_type]);

  // Pull the LabAide collection guide for ALL tests on the script in ONE
  // request (read-only view) — a single combined query returns one consolidated
  // answer instead of a separate API call per test. Cached per test-set.
  useEffect(() => {
    if (isEditing) return;
    const items = (Array.isArray(selectedTests) ? selectedTests : [])
      .map((t) => ({ name: t.name || t.alternative_name, code: t.code }))
      .filter((t) => t.name || t.code);
    if (items.length === 0) { setTubeGuide(null); return; }
    // Instant load: use the cached guide when the ordered test set is unchanged.
    const sig = tubeGuideSig(submission?.tests_on_the_script);
    const cached = submission?.tube_guide;
    if (cached && cached.sig === sig && cached.result) { setTubeGuide(cached.result); return; }
    const apiBase = process.env.REACT_APP_API_URL || '';
    let cancelled = false;
    setTubeGuide({ status: 'loading' });
    (async () => {
      const result = await lookupTubesForTests(items, { apiBase, source: 'admin-submission' });
      if (cancelled) return;
      setTubeGuide(result);
      // Persist so opens (phleb or admin) are instant afterward — good results only.
      if (result && result.status !== 'error' && submission?.id) {
        supabase.from('submissions').update({ tube_guide: { sig, result } }).eq('id', submission.id).then(() => {}, () => {});
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTests, isEditing]);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (specimenDropdownRef.current && !specimenDropdownRef.current.contains(e.target)) {
        setShowSpecimenDropdown(null);
      }
      if (doctorSearchRef.current && !doctorSearchRef.current.contains(e.target)) {
        setShowDoctorDropdown(false);
      }
      if (insuranceSearchRef.current && !insuranceSearchRef.current.contains(e.target)) {
        setShowInsuranceDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFedexModalOpen = () => {
    setFedexModalOpen(true);
  };

  const handleFedexModalClose = () => {
    setFedexModalOpen(false);
  };

  const handleLabelGenerated = async (labelData) => {
    try {
      const { error } = await supabase
        .from('submissions')
        .update({
          fedex_label_url: labelData.labelUrl,
          fedex_tracking_number: labelData.trackingNumber,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      setSubmission(prev => ({
        ...prev,
        fedex_label_url: labelData.labelUrl,
        fedex_tracking_number: labelData.trackingNumber
      }));

      toast({
        title: 'Label Generated',
        description: 'FedEx label created and saved successfully',
        status: 'success',
        duration: 5000,
        isClosable: true
      });

      setFedexModalOpen(false);
    } catch (error) {
      console.error('Error saving FedEx label:', error);
      toast({
        title: 'Error',
        description: 'Failed to save FedEx label data',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  const handleTrackShipment = async () => {
    if (!submission.fedex_tracking_number) {
      toast({
        title: 'No Tracking Number',
        description: 'No tracking number available for this submission',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    setTrackingLoading(true);
    try {
      const response = await fetch(`/api/track-fedex-shipment?trackingNumber=${submission.fedex_tracking_number}`, {
        headers: getAuthHeaders(),
      });
      const result = await response.json();

      if (result.success) {
        setTrackingInfo(result.tracking);
        toast({
          title: 'Tracking Info Retrieved',
          description: 'Shipment tracking information loaded',
          status: 'success',
          duration: 3000,
          isClosable: true
        });
      } else {
        throw new Error(result.error || 'Failed to track shipment');
      }
    } catch (error) {
      console.error('Error tracking shipment:', error);
      toast({
        title: 'Tracking Error',
        description: error.message || 'Failed to retrieve tracking information',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setTrackingLoading(false);
    }
  };

  useEffect(() => {
    const fetchSubmission = async () => {
      try {
        const { data: submissionData, error } = await supabase
          .from('submissions')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        setSubmission(submissionData);

        // Look up patient record — by FK, then email, then name match
        let patientResolved = false;
        let resolvedPatientUuid = null;

        // 1. Direct FK lookup (most reliable)
        if (submissionData.patient_id) {
          const { data: patientMatch } = await supabase
            .from('patients')
            .select('id,first_name,last_name,full_name,patient_id,patient_name')
            .eq('id', submissionData.patient_id)
            .single();
          if (patientMatch) {
            patientResolved = true;
            resolvedPatientUuid = patientMatch.id;
            if ((!submissionData.patient_name || submissionData.patient_name.trim() === '')) {
              const name = patientMatch.full_name || patientMatch.patient_name || [patientMatch.first_name, patientMatch.last_name].filter(Boolean).join(' ');
              if (name) setFallbackPatientName(name);
            }
            if (patientMatch.patient_id) setPatientIdNumber(patientMatch.patient_id);
          }
        }

        // 2. Email match fallback
        if (!patientResolved && submissionData.patient_email) {
          const { data: patientMatch } = await supabase
            .from('patients')
            .select('id,first_name,last_name,full_name,patient_id,patient_name')
            .ilike('patient_email', submissionData.patient_email)
            .limit(1);
          if (patientMatch && patientMatch.length > 0) {
            const p = patientMatch[0];
            patientResolved = true;
            resolvedPatientUuid = p.id;
            if ((!submissionData.patient_name || submissionData.patient_name.trim() === '')) {
              const name = p.full_name || p.patient_name || [p.first_name, p.last_name].filter(Boolean).join(' ');
              if (name) setFallbackPatientName(name);
            }
            if (p.patient_id) setPatientIdNumber(p.patient_id);
          }
        }

        // 3. Name match fallback — try exact patient_name, then first+last combo
        if (!patientResolved && submissionData.patient_name) {
          const trimmedName = submissionData.patient_name.trim();
          if (trimmedName) {
            // Try patient_name column first
            let { data: nameMatch } = await supabase
              .from('patients')
              .select('id,first_name,last_name,full_name,patient_id,patient_name')
              .ilike('patient_name', trimmedName)
              .eq('archived', false)
              .limit(1);

            // If no match, try matching against first_name + last_name
            if (!nameMatch || nameMatch.length === 0) {
              const parts = trimmedName.split(/\s+/);
              if (parts.length >= 2) {
                const firstName = parts[0];
                const lastName = parts.slice(1).join(' ');
                const { data: fnMatch } = await supabase
                  .from('patients')
                  .select('id,first_name,last_name,full_name,patient_id,patient_name')
                  .ilike('first_name', firstName)
                  .ilike('last_name', lastName)
                  .eq('archived', false)
                  .limit(1);
                nameMatch = fnMatch;
              }
            }

            if (nameMatch && nameMatch.length > 0) {
              const p = nameMatch[0];
              patientResolved = true;
              resolvedPatientUuid = p.id;
              if (p.patient_id) setPatientIdNumber(p.patient_id);
            }
          }
        }

        // If we resolved a patient but the submission has no patient_id FK, backfill it
        if (resolvedPatientUuid && !submissionData.patient_id) {
          await supabase
            .from('submissions')
            .update({ patient_id: resolvedPatientUuid })
            .eq('id', submissionData.id);
        }
        setEditForm(submissionData);
        
        // Load saved tests if they exist — resolve names from tests/panels tables
        if (submissionData.tests_on_the_script && Array.isArray(submissionData.tests_on_the_script)) {
          const savedTests = submissionData.tests_on_the_script;
          const ids = savedTests.map(t => t.id).filter(Boolean);

          // Check if any test is missing a name and needs resolving
          const needsResolve = ids.length > 0 && savedTests.some(t => !t.name);

          if (needsResolve) {
            try {
              const queries = [
                supabase.from('tests').select('id, name, code, description, cash_price').in('id', ids),
                supabase.from('panels').select('id, name, code, description, cash_price').in('id', ids),
              ];
              // Also fetch lab-specific prices if a lab is assigned
              if (submissionData.lab_id) {
                queries.push(
                  supabase.from('lab_test_offerings').select('test_id, lab_cash_price').eq('lab_id', submissionData.lab_id).in('test_id', ids),
                  supabase.from('lab_panel_offerings').select('panel_id, lab_cash_price').eq('lab_id', submissionData.lab_id).in('panel_id', ids),
                );
              }
              const [testsRes, panelsRes, labTestRes, labPanelRes] = await Promise.all(queries);
              const lookup = {};
              (testsRes.data || []).forEach(t => { lookup[t.id] = { name: t.name, code: t.code, description: t.description, cash_price: t.cash_price }; });
              (panelsRes.data || []).forEach(p => { lookup[p.id] = { name: p.name, code: p.code, description: p.description, cash_price: p.cash_price }; });
              // Lab-specific prices override global prices
              const labPriceLookup = {};
              (labTestRes?.data || []).forEach(o => { if (o.lab_cash_price != null) labPriceLookup[o.test_id] = parseFloat(o.lab_cash_price); });
              (labPanelRes?.data || []).forEach(o => { if (o.lab_cash_price != null) labPriceLookup[o.panel_id] = parseFloat(o.lab_cash_price); });
              const resolved = savedTests.map(t => {
                const info = lookup[t.id] || {};
                const labPrice = labPriceLookup[t.id];
                return {
                  ...t,
                  name: t.name || info.name || t.id,
                  description: t.description || info.description || '',
                  cash_price: labPrice ?? t.cash_price ?? t.price ?? info.cash_price ?? null,
                  price: labPrice ?? t.price ?? t.cash_price ?? info.cash_price ?? null,
                  code: t.code || info.code || '',
                };
              });
              setSelectedTests(resolved);
            } catch (resolveErr) {
              console.error('Error resolving test names:', resolveErr);
              setSelectedTests(savedTests);
            }
          } else {
            setSelectedTests(savedTests);
          }

          // Load diagnosis code confirmations
          const confirmations = {};
          savedTests.forEach(test => {
            confirmations[test.id] = test.diagnosis_code_present || false;
          });
          setDiagnosisCodesConfirmed(confirmations);
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchSubmission();
  }, [id, toast]);

  // Load the disposition history once we know the submission id. Re-loaded
  // by handleStatusLogged below whenever a new outcome is saved.
  const fetchStatusEvents = async (submissionId) => {
    if (!submissionId) return;
    setStatusEventsLoading(true);
    try {
      const { data, error } = await supabase
        .from('submission_status_events')
        .select('id, outcome_code, outcome_notes, outcome_flags, status, blood_collection_time, scheduling_type, availability_start_date, availability_end_date, availability_start_time, availability_end_time, logged_at, logged_by, logged_by_kind, logged_by_name')
        .eq('submission_id', submissionId)
        .order('logged_at', { ascending: false });
      if (error) throw error;
      setStatusEvents(data || []);
    } catch (e) {
      // Quiet failure — the panel falls back to "No history yet" rather than
      // blocking the whole page. (Migration may not have been applied yet.)
      console.warn('Could not load status events:', e.message);
      setStatusEvents([]);
    } finally {
      setStatusEventsLoading(false);
    }
  };

  useEffect(() => {
    if (submission?.id) fetchStatusEvents(submission.id);
  }, [submission?.id]);

  // Fetch patients for searchable dropdown
  useEffect(() => {
    const fetchPatients = async () => {
      setPatientsLoading(true);
      try {
        const sel = 'id, full_name, first_name, last_name' +
          (hasPatientsEmail ? ', email' : '') +
          (hasPatientsDob ? ', dob' : '') +
          (hasPatientsAddress ? ', address' : '');
        const { data, error } = await supabase
          .from('patients')
          .select(sel)
          .order('full_name');
        if (!error && data) setPatients(data);
      } finally {
        setPatientsLoading(false);
      }
    };
    fetchPatients();
  }, [hasPatientsEmail, hasPatientsAddress, hasPatientsDob]);

  const fetchDoctors = async () => {
    setDoctorsLoading(true);
    try {
      const { data, error } = await supabase
        .from('doctors')
        .select('id, full_name, email, phone, clinic_name, address')
        .order('full_name');
      
      if (error) {
        console.error('Error fetching doctors:', error);
      } else {
        setDoctors(data || []);
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
    } finally {
      setDoctorsLoading(false);
    }
  };

  const fetchInsurances = async () => {
    setInsurancesLoading(true);
    try {
      const { data, error } = await supabase
        .from('insurances')
        .select('id, name, type, description')
        .eq('is_active', true)
        .order('name');
      
      if (error) {
        console.error('Error fetching insurances:', error);
        toast({
          title: 'Error fetching insurances',
          description: error.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } else {
        setInsurances(data || []);
        console.log('Fetched insurances:', data?.length);
      }
    } catch (error) {
      console.error('Error fetching insurances:', error);
    } finally {
      setInsurancesLoading(false);
    }
  };

  const fetchAllRows = async (table, selectQuery, orderCol = 'name') => {
    const PAGE = 1000;
    let allData = [];
    let from = 0;
    while (true) {
      const { data, error } = await supabase
        .from(table).select(selectQuery).order(orderCol).range(from, from + PAGE - 1);
      if (error) throw error;
      if (!data || data.length === 0) break;
      allData = allData.concat(data);
      if (data.length < PAGE) break;
      from += PAGE;
    }
    return allData;
  };

  const fetchTests = async () => {
    setTestsLoading(true);
    try {
      const selectCols = 'id, name, code, description, cash_price, alternative_name';
      // Query both tests and panels tables with paginated fetch
      const [testsData, panelsData] = await Promise.all([
        fetchAllRows('tests', selectCols),
        fetchAllRows('panels', selectCols),
      ]);

      const allTests = [
        ...testsData.map(t => ({ ...t, type: 'test' })),
        ...panelsData.map(p => ({ ...p, type: 'panel' })),
      ].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      setTests(allTests);
    } catch (error) {
      console.error('Error fetching tests:', error);
      toast({
        title: 'Error fetching tests',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setTestsLoading(false);
    }
  };

  const handleToggleEdit = () => {
    if (!isEditing) {
      // Entering edit mode - initialize edit form with current submission data
      setEditForm({
        patient_name: submission.patient_name || '',
        patient_email: submission.patient_email || '',
        patient_dob: submission.patient_dob || '',
        patient_gender: submission.patient_gender || '',
        patient_address: submission.patient_address || '',
        patient_street: submission.patient_street || '',
        patient_city: submission.patient_city || '',
        patient_state: submission.patient_state || '',
        patient_zip: submission.patient_zip || '',
        doctor_name: submission.doctor_name || '',
        doctor_phone: submission.doctor_phone || '',
        doctor_email: submission.doctor_email || '',
        doctor_address: submission.doctor_address || '',
        doctor_fax: submission.doctor_fax || '',
        client_name: submission.client_name || '',
        insurance_company: submission.insurance_company || '',
        insurance_policy_number: submission.insurance_policy_number || '',
        lab_brand: submission.lab_brand || '',
        blood_collection_time: submission.blood_collection_time || '',
        phlebotomist_id: submission.phlebotomist_id || '',
        phlebotomist_name: submission.phlebotomist_name || '',
        phlebotomist_email: submission.phlebotomist_email || '',
        status: submission.status || 'pending'
      });
      // Pre-fill search fields so current values are visible
      setDoctorSearchQuery(submission?.doctor_name || '');
      setInsuranceSearchQuery(submission?.insurance_company || '');
      // Fetch all necessary data for dropdowns
      fetchDoctors();
      fetchInsurances();
      fetchTests();
      // Stay on current tab instead of jumping
    } else {
      // Leaving edit mode - no tab restriction
    }
    setIsEditing(!isEditing);
  };

  const handleAddNewDoctor = async () => {
    try {
      const { data: newDoctor, error } = await supabase
        .from('doctors')
        .insert(newDoctorForm)
        .select()
        .single();

      if (error) throw error;

      setDoctors([...doctors, newDoctor]);

      setEditForm({
        ...editForm,
        doctor_name: newDoctor.full_name,
        doctor_phone: newDoctor.phone,
        doctor_email: newDoctor.email,
        doctor_address: newDoctor.address,
        selectedDoctorId: newDoctor.id
      });

      setNewDoctorForm({
        full_name: '',
        email: '',
        phone: '',
        clinic_name: '',
        address: ''
      });

      setIsNewDoctor(false);

      toast({
        title: 'New doctor added successfully',
        description: `${newDoctor.full_name} has been added to the database`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error adding new doctor:', error);
      toast({
        title: 'Error adding new doctor',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleGeneratePaymentLink = () => {
    // Use clean payment portal link instead of raw Stripe URL
    if (!submission.draw_code) {
      toast({ title: 'No draw code', description: 'This submission has no draw code. Cannot generate payment link.', status: 'error', duration: 5000, isClosable: true });
      return;
    }
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/pay/${submission.draw_code}`;
    setPaymentLink(link);
    navigator.clipboard.writeText(link);
    toast({ title: 'Payment link copied!', description: link, status: 'success', duration: 4000, isClosable: true });
  };

  const handleSaveChanges = async () => {
    try {
      let updateData = { ...editForm };
      
      // If structured address fields are present, synthesize combined address
      const street = (updateData.patient_street || '').trim();
      const city = (updateData.patient_city || '').trim();
      const state = (updateData.patient_state || '').trim();
      const zip = (updateData.patient_zip || '').trim();
      if (street || city || state || zip) {
        const cityState = [city, state].filter(Boolean).join(', ');
        const tail = [cityState, zip].filter(Boolean).join(' ');
        const combined = [street, tail].filter(Boolean).join(', ');
        if (combined) updateData.patient_address = combined;
      }

      // Normalize DOB to YYYY-MM-DD string
      if (updateData.patient_dob && typeof updateData.patient_dob !== 'string') {
        try {
          const d = new Date(updateData.patient_dob);
          if (!isNaN(d.getTime())) {
            updateData.patient_dob = d.toISOString().slice(0, 10);
          }
        } catch (_) {}
      }
      
      // Remove UI-only fields
      delete updateData.selectedDoctorId;
      delete updateData.selectedInsuranceId;
      delete updateData.selectedPatientId;

      // Remove read-only fields that should never be updated.
      // (phlebotomist_id / _name / _email used to be in this list; they're now
      // admin-editable from the Phlebotomist picker in the teal banner.)
      const readOnlyFields = [
        'id',
        'created_at',
        'updated_at',
        'submitted_at',
      ];
      readOnlyFields.forEach(field => delete updateData[field]);

      // Remove only undefined values — keep null and '' so fields can be cleared
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      // Normalize empty strings to null on nullable columns so Postgres doesn't
      // reject '' for UUID fields (the phleb picker clears via empty string).
      ['phlebotomist_id', 'phlebotomist_name', 'phlebotomist_email'].forEach(field => {
        if (updateData[field] === '') updateData[field] = null;
      });

      // Validate UUID fields if present
      const uuidFields = ['lab_id', 'doctor_id', 'insurance_id', 'patient_id', 'phlebotomist_id'];
      uuidFields.forEach(field => {
        if (updateData[field] && typeof updateData[field] === 'string') {
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
          if (!uuidRegex.test(updateData[field])) {
            console.warn(`Invalid UUID format for ${field}:`, updateData[field]);
            delete updateData[field];
          }
        }
      });

      // Save selected tests with diagnosis code confirmations as JSONB
      if (selectedTests.length > 0) {
        const testsData = selectedTests.map(test => ({
          id: test.id,
          name: test.name,
          description: test.description,
          price: test.cash_price ?? test.price,
          diagnosis_code_present: diagnosisCodesConfirmed[test.id] || false
        }));
        updateData.tests_on_the_script = testsData;
      } else if (selectedTests.length === 0 && editForm.tests_on_the_script === undefined) {
        // If no tests selected and tests_on_the_script wasn't in editForm, set to empty array
        updateData.tests_on_the_script = [];
      }

      // Serialize specimen rows to JSON
      const validSpecimens = specimenRows.filter(s => s.type);
      if (validSpecimens.length > 0) {
        updateData.specimen_type = JSON.stringify(validSpecimens);
      }

      // Add updated_at timestamp
      updateData.updated_at = new Date().toISOString();

      // If a patient is not selected in dropdown but details are filled, upsert the patient first
      if (!editForm.selectedPatientId && (editForm.patient_name || editForm.patient_email)) {
        try {
          await addNewPatientFromFields();
        } catch (_) { /* non-fatal to submission save */ }
      }

      console.log('Updating submission:', { id: submission.id, updateData });

      // Handle both numeric and UUID IDs
      const submissionId = typeof submission.id === 'string' && !isNaN(submission.id) 
        ? parseInt(submission.id) 
        : submission.id;

      const { error, data } = await supabase
        .from('submissions')
        .update(updateData)
        .eq('id', submissionId)
        .select();

      if (error) {
        const errorDetails = {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
          updateDataKeys: Object.keys(updateData),
          updateDataSample: Object.fromEntries(
            Object.entries(updateData).slice(0, 10).map(([k, v]) => [
              k, 
              typeof v === 'object' && v !== null ? JSON.stringify(v).substring(0, 100) : String(v).substring(0, 100)
            ])
          )
        };
        console.error('Update error details:', errorDetails);
        console.error('Full updateData:', updateData);
        throw error;
      }

      setSubmission(prev => ({ ...prev, ...updateData }));
      
      // Also save tests and confirmations to state
      if (updateData.tests_on_the_script) {
        setSelectedTests(updateData.tests_on_the_script);
        const confirmations = {};
        updateData.tests_on_the_script.forEach(test => {
          confirmations[test.id] = test.diagnosis_code_present;
        });
        setDiagnosisCodesConfirmed(confirmations);
      }
      
      setIsEditing(false);
      
      toast({
        title: 'Changes saved successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error saving changes',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };


  const handleCancelEdit = async () => {
    // Cancel edit mode and reset form
    setIsEditing(false);
    setEditForm({});
    setIsNewDoctor(false);
    setIsNewInsurance(false);
    setDoctorSearchQuery('');
    setShowDoctorDropdown(false);
    setInsuranceSearchQuery('');
    setShowInsuranceDropdown(false);
    setTestSearchQuery('');
    setShowTestDropdown(false);
    setNewDoctorForm({
      full_name: '',
      email: '',
      phone: '',
      clinic_name: '',
      address: ''
    });
    setNewInsuranceForm({
      company_name: '',
      provider_type: '',
      policy_number: ''
    });

    // Restore selectedTests from the saved submission data
    if (submission?.tests_on_the_script && Array.isArray(submission.tests_on_the_script)) {
      const savedTests = submission.tests_on_the_script;
      const ids = savedTests.map(t => t.id).filter(Boolean);
      const needsResolve = ids.length > 0 && savedTests.some(t => !t.name);
      if (needsResolve) {
        try {
          const [testsRes, panelsRes] = await Promise.all([
            supabase.from('tests').select('id, name, code, description, cash_price').in('id', ids),
            supabase.from('panels').select('id, name, code, description, cash_price').in('id', ids),
          ]);
          const lookup = {};
          (testsRes.data || []).forEach(t => { lookup[t.id] = { name: t.name, code: t.code, description: t.description, cash_price: t.cash_price }; });
          (panelsRes.data || []).forEach(p => { lookup[p.id] = { name: p.name, code: p.code, description: p.description, cash_price: p.cash_price }; });
          setSelectedTests(savedTests.map(t => {
            const info = lookup[t.id] || {};
            return { ...t, name: t.name || info.name || t.id, description: t.description || info.description || '', cash_price: t.cash_price ?? info.cash_price ?? null, code: t.code || info.code || '' };
          }));
        } catch {
          setSelectedTests(savedTests);
        }
      } else {
        setSelectedTests(savedTests);
      }
    }
  };

  const filteredDoctors = doctors.filter(doctor => {
    const query = doctorSearchQuery.toLowerCase();
    return doctor.full_name?.toLowerCase().includes(query) || 
           doctor.clinic_name?.toLowerCase().includes(query) ||
           doctor.email?.toLowerCase().includes(query);
  });

  const selectDoctor = (doctor) => {
    setEditForm({
      ...editForm,
      doctor_name: doctor.full_name,
      doctor_phone: doctor.phone || '',
      doctor_email: doctor.email || '',
      doctor_address: doctor.address || '',
      client_name: doctor.clinic_name || editForm.client_name || '',
      selectedDoctorId: doctor.id
    });
    setDoctorSearchQuery(doctor.full_name);
    setShowDoctorDropdown(false);
  };

  const filteredInsurances = insurances.filter(insurance => {
    const query = insuranceSearchQuery.toLowerCase();
    return insurance.name?.toLowerCase().includes(query) || 
           insurance.type?.toLowerCase().includes(query);
  });

  const selectInsurance = (insurance) => {
    setEditForm({
      ...editForm,
      insurance_company: insurance.name,
      insurance_policy_number: insurance.policy_number || '',
      selectedInsuranceId: insurance.id
    });
    setInsuranceSearchQuery(insurance.name);
    setShowInsuranceDropdown(false);
  };

  const matchesAcronym = (name, search) => {
    if (!name || !search) return false;
    const words = name.split(/\s+/).filter(w => w.length > 0);
    if (words.length < 2) return false;
    const acronym = words.map(w => w[0]).join('').toLowerCase();
    return acronym === search || acronym.startsWith(search);
  };

  const filteredTests = tests.filter(test => {
    const query = testSearchQuery.toLowerCase();
    return test.name?.toLowerCase().includes(query) ||
           test.alternative_name?.toLowerCase().includes(query) ||
           matchesAcronym(test.name, query);
  });

  const addTest = (test) => {
    if (!selectedTests.find(t => t.id === test.id)) {
      setSelectedTests([...selectedTests, test]);
    }
    setTestSearchQuery('');
    setShowTestDropdown(false);
  };

  const removeTest = (testId) => {
    setSelectedTests(selectedTests.filter(t => t.id !== testId));
    // Also remove the diagnosis code confirmation for this test
    const newConfirmations = { ...diagnosisCodesConfirmed };
    delete newConfirmations[testId];
    setDiagnosisCodesConfirmed(newConfirmations);
  };

  const toggleDiagnosisCodeConfirmed = (testId) => {
    setDiagnosisCodesConfirmed({
      ...diagnosisCodesConfirmed,
      [testId]: !diagnosisCodesConfirmed[testId]
    });
  };

  // Upload document files (script, insurance card, patient ID)
  const handleDocUpload = async (files, type) => {
    // type: 'script_image' | 'insurance_card_image' | 'patient_id_image'
    const uploadKey = type === 'script_image' ? 'script' : type === 'insurance_card_image' ? 'insurance' : 'patientId';
    setDocUploading(prev => ({ ...prev, [uploadKey]: true }));
    try {
      const urls = [];
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const folder = type === 'script_image' ? 'script' : type === 'insurance_card_image' ? 'insurance' : 'patientId';
        const filePath = `${folder}/${fileName}`;

        const { error } = await supabase.storage
          .from('uploads')
          .upload(filePath, file, { contentType: file.type || 'application/octet-stream', upsert: true });

        if (error) {
          console.error('Upload error:', error);
          toast({ title: 'Upload Error', description: error.message, status: 'error', duration: 5000, isClosable: true });
          continue;
        }

        const { data: { publicUrl } } = supabase.storage.from('uploads').getPublicUrl(filePath);
        urls.push(publicUrl);
      }

      if (urls.length === 0) return;

      // Merge with existing URLs
      let existing = submission[type];
      if (typeof existing === 'string') { try { existing = JSON.parse(existing); } catch { existing = existing ? [existing] : []; } }
      if (!Array.isArray(existing)) existing = existing ? [existing] : [];
      const merged = [...existing.filter(Boolean), ...urls];

      const { error } = await supabase.from('submissions').update({ [type]: merged }).eq('id', submission.id);
      if (error) throw error;

      setSubmission(prev => ({ ...prev, [type]: merged }));
      toast({ title: 'Uploaded', description: `${urls.length} file(s) uploaded successfully`, status: 'success', duration: 3000, isClosable: true });
    } catch (err) {
      console.error('Document upload error:', err);
      toast({ title: 'Upload Failed', description: err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setDocUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const fetchAvailableLabs = async () => {
    if (selectedTests.length === 0) {
      setAvailableLabs([]);
      return;
    }

    setAvailableLabsLoading(true);
    try {
      const testIds = selectedTests.map(test => test.id);
      // Guard against legacy placeholder IDs like "test1" which are not UUIDs
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      const validTestIds = testIds.filter(id => typeof id === 'string' && uuidRegex.test(id));
      if (validTestIds.length === 0) {
        setAvailableLabs([]);
        return;
      }
      
      // Query labs that offer these tests
      const { data, error } = await supabase
        .from('lab_test_offerings')
        .select(`
          *,
          labs (
            id,
            name,
            address,
            city,
            state,
            zip_code,
            contact_phone,
            contact_email,
            is_active
          )
        `)
        .in('test_id', validTestIds);

      if (error) throw error;

      // Organize the data by lab
      const labsMap = {};
      
      data?.forEach(offering => {
        const lab = offering.labs;
        if (!lab || !lab.id) return; // Skip orphaned offerings with no lab
        if (!labsMap[lab.id]) {
          labsMap[lab.id] = {
            lab: lab,
            tests: [],
            totalPrice: 0
          };
        }
        
        const testInfo = selectedTests.find(t => t.id === offering.test_id);
        if (testInfo) {
          labsMap[lab.id].tests.push({
            ...testInfo,
            lab_price: offering.lab_cash_price,
            lab_test_code: offering.lab_test_code
          });
          labsMap[lab.id].totalPrice += parseFloat(offering.lab_cash_price || 0);
        }
      });

      setAvailableLabs(Object.values(labsMap));
      
    } catch (error) {
      console.error('Error fetching available labs:', error);
      toast({
        title: 'Error fetching labs',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setAvailableLabsLoading(false);
    }
  };

  // Fetch labs when selectedTests changes
  useEffect(() => {
    if (selectedTests.length > 0) {
      fetchAvailableLabs();
    }
  }, [selectedTests]);

  const handleAddNewInsurance = async () => {
    try {
      // Map form fields to database schema
      // Database uses 'name' not 'company_name', and 'type' not 'provider_type'
      const insuranceData = {
        name: newInsuranceForm.company_name,
        type: newInsuranceForm.provider_type || 'Commercial',
        description: null,
        is_active: true
      };

      const { data: newInsurance, error } = await supabase
        .from('insurances')
        .insert(insuranceData)
        .select()
        .single();

      if (error) throw error;

      // Add to the insurances list for the dropdown
      setInsurances([...insurances, newInsurance]);

      // Update the edit form to set this as the selected insurance
      const updatedEditForm = {
        ...editForm,
        insurance_company: newInsurance.name, // Database uses 'name'
        insurance_policy_number: newInsuranceForm.policy_number || '',
        selectedInsuranceId: newInsurance.id,
        insurance_id: newInsurance.id // Also set insurance_id if the field exists
      };

      setEditForm(updatedEditForm);

      // Immediately save to the submission so it's linked
      if (submission && submission.id) {
        const updateData = {
          insurance_company: newInsurance.name,
          insurance_policy_number: newInsuranceForm.policy_number || null, // Use null instead of empty string
          insurance_id: newInsurance.id,
          updated_at: new Date().toISOString()
        };

        // Remove undefined values only (keep null for optional fields)
        Object.keys(updateData).forEach(key => {
          if (updateData[key] === undefined) {
            delete updateData[key];
          }
        });

        const { error: updateError } = await supabase
          .from('submissions')
          .update(updateData)
          .eq('id', submission.id);

        if (updateError) {
          console.error('Error updating submission with new insurance:', updateError);
          // Don't throw - we still want to show success for creating the insurance
          toast({
            title: 'Insurance created, but failed to link',
            description: 'Please manually save the submission to link the insurance',
            status: 'warning',
            duration: 4000,
            isClosable: true,
          });
        } else {
          // Update local submission state
          setSubmission(prev => ({
            ...prev,
            ...updateData
          }));

          toast({
            title: 'Insurance carrier added and linked',
            description: `${newInsurance.name} has been added to the database and linked to this submission`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }
      } else {
        toast({
          title: 'New insurance carrier added successfully',
          description: `${newInsurance.name} has been added to the database. Don't forget to save the submission.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      // Reset form
      setNewInsuranceForm({
        company_name: '',
        provider_type: '',
        policy_number: ''
      });

      setIsNewInsurance(false);
    } catch (error) {
      console.error('Error adding new insurance:', error);
      toast({
        title: 'Error adding new insurance',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status) => getSubmissionStatusColor(status);

  const checkTabCompleteness = (tabName) => {
    if (!submission) return false;
    
    switch(tabName) {
      case 'patient':
        return !!(submission.patient_name && submission.patient_dob);
      case 'doctor':
        // Consider doctor step complete if a doctor has been selected or a name is provided
        return !!(submission.doctor_name);
      case 'insurance':
        if (submission.is_self_pay) return true;
        return !!((submission.insurance_company || submission.insurance_id) && (submission.insurance_policy_number || submission.policy_number));
      case 'script':
        return !!submission.blood_collection_time;
      case 'available_labs':
        // Consider complete when a lab is chosen (lab_id set by phlebotomist)
        return !!submission.lab_id;
      case 'phlebotomist':
        return !!(submission.phlebotomist_name && submission.phlebotomist_email);
      case 'fedex':
        return !!(submission.fedex_label_url);
      case 'dropoff':
        return !!(submission.dropped_off_at || submission.picked_up_at);
      case 'results':
        return !!(submission.lab_results_url);
      default:
        return true;
    }
  };

  // Define tab order for progress and navigation
  const tabOrder = [
    { key: 'patient', label: 'Patient Info' },
    { key: 'doctor', label: 'Doctor Info' },
    { key: 'insurance', label: 'Insurance' },
    { key: 'script', label: 'Script' },
  { key: 'available_labs', label: 'Available Labs' },
  { key: 'dropoff', label: 'Drop-off / Pickup' },
    { key: 'results', label: 'Results' },
  ];
  // Auto-open Available Labs when first 4 steps are complete
  const firstFourComplete = ['patient', 'doctor', 'insurance', 'script'].every((k) => checkTabCompleteness(k));
  const availableLabsTabIndex = tabOrder.findIndex(t => t.key === 'available_labs');

  // Only auto-navigate to Available Labs ONCE when the first four steps just became complete
  const [autoNavigatedLabs, setAutoNavigatedLabs] = useState(false);

  useEffect(() => {
    if (firstFourComplete && !autoNavigatedLabs) {
      setActiveTabIndex(availableLabsTabIndex);
      setAutoNavigatedLabs(true);
    }
  }, [firstFourComplete, availableLabsTabIndex, autoNavigatedLabs]);

  // Reset the auto-navigation flag if any of the first four steps becomes incomplete later
  useEffect(() => {
    if (!firstFourComplete && autoNavigatedLabs) {
      setAutoNavigatedLabs(false);
    }
  }, [firstFourComplete, autoNavigatedLabs]);

  // Load selected lab details when lab_id exists/changes
  useEffect(() => {
    const loadSelectedLab = async () => {
      if (!submission?.lab_id) {
        setSelectedLabDetails(null);
        return;
      }
      try {
        setSelectedLabLoading(true);
        const { data, error } = await supabase
          .from('labs')
          .select('id, name, address, city, state, zip_code, contact_email, contact_phone')
          .eq('id', submission.lab_id)
          .single();
        if (!error) setSelectedLabDetails(data);
      } finally {
        setSelectedLabLoading(false);
      }
    };
    loadSelectedLab();
  }, [submission?.lab_id]);

  // FedEx shipping is replaced by the Drop-off / Pickup section. The old FedEx
  // UI is kept in the source (behind this flag) for an easy rollback — set
  // REACT_APP_SHOW_FEDEX=true to bring it back.
  const SHOW_FEDEX_SECTION = process.env.REACT_APP_SHOW_FEDEX === 'true';

  // ── Drop-off locations (managed list) + handoff marking ──────────────────
  const [dropOffLocations, setDropOffLocations] = useState([]);
  const [handoffSaving, setHandoffSaving] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('drop_off_locations')
        .select('id, name, address, active')
        .eq('active', true)
        .order('name', { ascending: true });
      if (cancelled) return;
      if (!error) setDropOffLocations(data || []);
    })();
    return () => { cancelled = true; };
  }, []);

  // Persist handoff fields immediately (operational action, independent of the
  // page-wide edit mode). Patch is merged into the submission row + local state.
  const updateHandoff = async (patch) => {
    if (!submission?.id) return;
    setHandoffSaving(true);
    try {
      const { error } = await supabase
        .from('submissions')
        .update(patch)
        .eq('id', submission.id);
      if (error) throw error;
      setSubmission((prev) => ({ ...prev, ...patch }));
      toast({ title: 'Drop-off / pickup updated', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({ title: 'Failed to update', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setHandoffSaving(false);
    }
  };

  // Manual orders: admin-entered Order ID (lab_order_number). Saved immediately
  // on blur; the phleb sees it as "Order #…" just like lab-API orders.
  const saveOrderId = async (value) => {
    if (!submission?.id) return;
    const v = (value || '').trim();
    if (v === (submission.lab_order_number || '')) return; // no change
    try {
      const { error } = await supabase
        .from('submissions')
        .update({ lab_order_number: v || null })
        .eq('id', submission.id);
      if (error) throw error;
      setSubmission((prev) => ({ ...prev, lab_order_number: v || null }));
      toast({ title: 'Order ID saved', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({ title: 'Failed to save Order ID', description: err.message, status: 'error', duration: 4000, isClosable: true });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="100vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (!submission) {
    return (
      <Box p={8}>
        <Text>Submission not found</Text>
        <Button mt={4} onClick={() => navigate('/admin/submissions')}>
          Back to Submissions
        </Button>
      </Box>
    );
  }

  // Progress helpers
  const completedCount = tabOrder.reduce((acc, t) => acc + (checkTabCompleteness(t.key) ? 1 : 0), 0);
  const nextIncompleteIndex = tabOrder.findIndex(t => !checkTabCompleteness(t.key));
  const progressPercent = Math.round((completedCount / tabOrder.length) * 100);

  

  const handleProvideLabSelection = async () => {
    try {
      setSendingSelection(true);
      const { error } = await supabase
        .from('submissions')
        .update({ status: 'in_progress', updated_at: new Date().toISOString() })
        .eq('id', submission.id);
      if (error) throw error;
      setSelectionSent(true);
      toast({
        title: 'Next step started',
        description: 'Waiting for phlebotomist to choose an available lab.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      toast({ title: 'Error', description: e.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSendingSelection(false);
    }
  };

  // Patient search helpers
  const filteredPatients = patients.filter((p) => {
    const q = patientSearchQuery.toLowerCase();
    const name = p.full_name || [p.first_name, p.last_name].filter(Boolean).join(' ');
    return (
      (name || '').toLowerCase().includes(q) ||
      (p.email || '').toLowerCase().includes(q)
    );
  });

  const addNewPatientFromFields = async () => {
    try {
      const fullName = (editForm.patient_name || '').trim();
      const email = (editForm.patient_email || '').trim();
      const dob = editForm.patient_dob || null;
      const address = editForm.patient_address || '';
      if (!fullName && !email) {
        toast({ title: 'Enter patient info first', description: 'Provide at least a name or email.', status: 'warning', duration: 3000, isClosable: true });
        return;
      }
      // Try to upsert by email if provided
      let createdId = null;
      if (email && hasPatientsEmail) {
        const { data: existing } = await supabase.from('patients').select('id').ilike('email', email).limit(1);
        if (existing && existing.length > 0) {
          createdId = existing[0].id;
          // Try update including address; if it fails due to schema, retry without address
          let updateErr = null;
          const payloadUpd = { full_name: fullName };
          if (hasPatientsEmail) payloadUpd.email = email;
          if (hasPatientsDob) payloadUpd.dob = dob;
          if (hasPatientsAddress) payloadUpd.address = address;
          let upd = await supabase.from('patients').update(payloadUpd).eq('id', createdId);
          updateErr = upd.error;
          if (updateErr && /address/.test(updateErr.message || '')) {
            await supabase.from('patients').update({ full_name: fullName, email, dob }).eq('id', createdId);
          } else if (updateErr) {
            throw updateErr;
          }
        } else {
          // Try insert with address; if it fails for schema, retry without address
          let inserted = null; let insErr = null;
          const payloadIns = { full_name: fullName };
          if (hasPatientsEmail) payloadIns.email = email;
          if (hasPatientsDob) payloadIns.dob = dob;
          if (hasPatientsAddress) payloadIns.address = address;
          ({ data: inserted, error: insErr } = await supabase
            .from('patients')
            .insert(payloadIns)
            .select('id')
            .single());
          if (insErr && /address/.test(insErr.message || '')) {
            const retry = await supabase
              .from('patients')
              .insert({ full_name: fullName, email, dob })
              .select('id')
              .single();
            if (retry.error) throw retry.error;
            createdId = retry.data.id;
          } else if (insErr) {
            throw insErr;
          } else if (inserted) {
            createdId = inserted.id;
          }
        }
      } else {
        // Insert without email; try with address then fallback
        let inserted = null; let insErr = null;
        const payloadIns2 = { full_name: fullName };
        if (hasPatientsDob) payloadIns2.dob = dob;
        if (hasPatientsAddress) payloadIns2.address = address;
        ({ data: inserted, error: insErr } = await supabase
          .from('patients')
          .insert(payloadIns2)
          .select('id')
          .single());
        if (insErr && /address/.test(insErr.message || '')) {
          const retry = await supabase
            .from('patients')
            .insert({ full_name: fullName, dob })
            .select('id')
            .single();
        if (retry.error) throw retry.error;
          createdId = retry.data.id;
        } else if (insErr) {
          throw insErr;
        } else if (inserted) {
          createdId = inserted.id;
        }
      }

      // Refresh patients list and select
      const { data: refreshed } = await supabase
        .from('patients')
        .select('id, full_name, first_name, last_name, email, dob, address')
        .order('full_name');
      if (refreshed) setPatients(refreshed);

      setEditForm({ ...editForm, selectedPatientId: createdId });
      setPatientSearchQuery(fullName);
      setShowPatientDropdown(false);
      toast({ title: 'Patient saved', status: 'success', duration: 2000, isClosable: true });
    } catch (e) {
      toast({ title: 'Failed to save patient', description: e.message, status: 'error', duration: 4000, isClosable: true });
    }
  };

  const selectPatient = (p) => {
    const name = p.full_name || [p.first_name, p.last_name].filter(Boolean).join(' ');
    setEditForm({
      ...editForm,
      patient_name: name || '',
      patient_email: p.email || '',
      patient_dob: p.dob || '',
      patient_address: p.address || '',
      patient_street: p.street || '',
      patient_city: p.city || '',
      patient_state: p.state || '',
      patient_zip: p.zip || '',
      selectedPatientId: p.id,
    });
    setPatientSearchQuery(name || '');
    setShowPatientDropdown(false);
  };

  // Specimen multi-row editor (reused in both edit mode and read-only Script tab)
  const renderSpecimenEditor = () => (
    <Box ref={specimenDropdownRef}>
      {specimenRows.map((row, idx) => (
        <HStack key={idx} spacing={2} mb={2}>
          <Select
            size="sm"
            w="80px"
            value={row.quantity}
            onChange={(e) => {
              const updated = [...specimenRows];
              updated[idx] = { ...updated[idx], quantity: parseInt(e.target.value) };
              setSpecimenRows(updated);
            }}
          >
            {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
          </Select>
          <Box flex={1} position="relative">
            <Input
              size="sm"
              placeholder="Search specimen type..."
              value={showSpecimenDropdown === idx ? (specimenSearches[idx] ?? row.type) : row.type}
              onChange={(e) => {
                setSpecimenSearches({ ...specimenSearches, [idx]: e.target.value });
                setShowSpecimenDropdown(idx);
              }}
              onFocus={() => {
                setSpecimenSearches({ ...specimenSearches, [idx]: row.type });
                setShowSpecimenDropdown(idx);
              }}
            />
            {showSpecimenDropdown === idx && (() => {
              const searchVal = specimenSearches[idx] || '';
              const filtered = searchVal
                ? specimenTypes.filter(s => s.toLowerCase().includes(searchVal.toLowerCase()))
                : specimenTypes;
              return filtered.length > 0 ? (
                <Box
                  position="absolute" zIndex={20} w="100%" maxH="200px" overflowY="auto"
                  bg="white" border="1px solid" borderColor="gray.300" borderRadius="md" mt={1} boxShadow="md"
                >
                  {filtered.map((st) => (
                    <Box
                      key={st} p={2} cursor="pointer" fontSize="sm"
                      _hover={{ bg: 'blue.50' }}
                      bg={row.type === st ? 'blue.100' : undefined}
                      onClick={() => {
                        const updated = [...specimenRows];
                        updated[idx] = { ...updated[idx], type: st };
                        setSpecimenRows(updated);
                        setShowSpecimenDropdown(null);
                        setSpecimenSearches({ ...specimenSearches, [idx]: '' });
                      }}
                    >
                      {st}
                    </Box>
                  ))}
                </Box>
              ) : null;
            })()}
          </Box>
          {specimenRows.length > 1 && (
            <IconButton
              icon={<CloseIcon />} size="xs" variant="ghost" colorScheme="red"
              aria-label="Remove specimen row"
              onClick={() => {
                const updated = specimenRows.filter((_, i) => i !== idx);
                setSpecimenRows(updated);
                // Clean up search state for removed row
                const newSearches = {};
                Object.keys(specimenSearches).forEach(k => {
                  const ki = parseInt(k);
                  if (ki < idx) newSearches[ki] = specimenSearches[ki];
                  else if (ki > idx) newSearches[ki - 1] = specimenSearches[ki];
                });
                setSpecimenSearches(newSearches);
              }}
            />
          )}
        </HStack>
      ))}
      <Button
        size="xs" variant="ghost" colorScheme="blue" leftIcon={<AddIcon />}
        onClick={() => setSpecimenRows([...specimenRows, { quantity: 1, type: '' }])}
      >
        Add Specimen Row
      </Button>
    </Box>
  );

  return (
    <Box minH="100vh" bg="gray.50">
      {/* ── Modern sticky command bar ─────────────────────────────────────
          Everything you need to identify and act on the submission in one slim
          bar that stays pinned while you scroll: id, status, who's involved,
          and the primary actions. Replaces the old back-bar + tall intro card. */}
      <Box position="sticky" top={0} zIndex={30} bg="white" borderBottomWidth={1} borderColor="gray.200" boxShadow="sm">
        <Flex px={4} py={2} align="center" gap={3} flexWrap="wrap">
          <IconButton
            aria-label="Back to submissions"
            icon={<ArrowBackIcon />}
            size="sm"
            variant="ghost"
            onClick={() => {
              if (isEditing) {
                const confirmLeave = window.confirm('You have unsaved changes. Are you sure you want to leave?');
                if (!confirmLeave) return;
              }
              navigate('/admin/submissions');
            }}
          />
          <Heading size="sm" color="gray.800" whiteSpace="nowrap">Submission</Heading>
          <Badge bg="blue.50" color="blue.600" fontFamily="mono" fontWeight="700" px={2} borderRadius="md">
            #{submission.id}
          </Badge>
          <Badge colorScheme={
            submission.status === 'completed' ? 'green' :
            submission.status === 'in_progress' ? 'blue' :
            submission.status === 'cancelled' ? 'red' :
            submission.status === 'waiting_on_lab_results' ? 'orange' : 'yellow'
          } borderRadius="full" px={2} py={0.5} textTransform="capitalize">
            {(submission.status || 'pending').replace(/_/g, ' ')}
          </Badge>
          <Text fontSize="sm" fontWeight="600" color="gray.700" noOfLines={1} maxW="240px">
            {(submission.patient_name && submission.patient_name.trim()) ? submission.patient_name : (fallbackPatientName || '—')}
          </Text>

          <HStack spacing={2} ml="auto">
            {isEditing ? (
              <>
                <Button size="sm" colorScheme="green" onClick={handleSaveChanges} leftIcon={<EditIcon />}>Save</Button>
                <Button size="sm" variant="ghost" onClick={handleCancelEdit}>Cancel</Button>
              </>
            ) : (
              <Button size="sm" colorScheme="blue" onClick={handleToggleEdit} leftIcon={<EditIcon />}>Edit</Button>
            )}
            <Button size="sm" variant="outline" colorScheme="purple" onClick={startMerge}>Merge…</Button>
          </HStack>
        </Flex>

        {/* one-line who's-who summary */}
        <Flex px={4} pb={1.5} gap={5} flexWrap="wrap" fontSize="xs" color="gray.500">
          {[
            ['Doctor', submission.doctor_name || '—', false],
            ['Phleb', resolvePhlebName(submission) || (submission.phlebotomist_id ? 'Unknown' : 'Unassigned'), !submission.phlebotomist_id],
            ['Lab', submission.lab_brand || selectedLabDetails?.name || '—', false],
          ].map(([label, value, warn]) => (
            <HStack key={label} spacing={1.5}>
              <Text textTransform="uppercase" letterSpacing="0.04em" fontWeight="700">{label}</Text>
              <Text fontWeight="600" color={warn ? 'red.500' : 'gray.700'}>{value}</Text>
            </HStack>
          ))}
        </Flex>
      </Box>

      <Box px={4} py={3} bg="gray.50">
        <Flex gap={3} align="flex-start" direction={{ base: "column", md: "row" }}>
          {/* ── Persistent left rail: status + notes/activity timeline ──── */}
          <Box
            w={{ base: "100%", md: "320px", lg: "360px" }}
            flexShrink={0}
            position={{ md: "sticky" }}
            top="84px"
            maxH={{ md: "calc(100vh - 96px)" }}
            overflowY={{ md: "auto" }}
          >
            {/* ── Current Status + History ──────────────────────────────
                Prominent "what's happening now" block on top — big badges,
                live 3-hour SLA countdown so the admin always knows how
                overdue the next update is, and one big button to add a note
                or change disposition. History list collapses below. */}
            {(() => {
              // "Latest disposition" drives the status header + SLA clock — pick the
              // most recent event carrying an outcome_code, so note-only events (e.g.
              // a tube-guide confirmation) don't masquerade as the current status or
              // reset the SLA timer. They still appear in the history list below.
              const latest = statusEvents.find((e) => e.outcome_code) || null;
              const historyEvents = statusEvents.filter((ev) => ev.id !== latest?.id);
              const fallbackOutcome = !latest && submission.outcome_code ? submission.outcome_code : null;
              const fallbackNote = !latest && submission.outcome_notes ? submission.outcome_notes : null;
              const lastUpdateTs = latest?.logged_at
                ? new Date(latest.logged_at)
                : (submission.outcome_logged_at ? new Date(submission.outcome_logged_at) : null);
              const cfgNow = latest?.outcome_code
                ? getOutcomeConfig(latest.outcome_code)
                : (fallbackOutcome ? getOutcomeConfig(fallbackOutcome) : null);

              // 3-hour SLA: green up to 2h, yellow 2-3h, red past 3h. Computed
              // from `now` (not memoized) so the 30s tick re-renders this scope.
              const SLA_HOURS = 3;
              const now = new Date();
              const elapsedMs = lastUpdateTs ? (now - lastUpdateTs) : null;
              const elapsedHours = elapsedMs != null ? elapsedMs / 3_600_000 : null;
              const overdue = elapsedHours != null && elapsedHours >= SLA_HOURS;
              const dueSoon = elapsedHours != null && elapsedHours >= SLA_HOURS - 1 && !overdue;
              const slaColor = overdue ? 'red' : dueSoon ? 'orange' : (elapsedHours == null ? 'gray' : 'green');

              const fmtElapsed = (ms) => {
                if (ms == null) return 'never';
                const totalMin = Math.max(0, Math.round(ms / 60000));
                if (totalMin < 1) return 'just now';
                if (totalMin < 60) return `${totalMin}m ago`;
                const h = Math.floor(totalMin / 60);
                const m = totalMin % 60;
                return m === 0 ? `${h}h ago` : `${h}h ${m}m ago`;
              };
              const fmtRemaining = () => {
                if (elapsedHours == null) return 'No updates yet — log one now';
                if (overdue) {
                  const overMs = elapsedMs - SLA_HOURS * 3_600_000;
                  const overMin = Math.round(overMs / 60000);
                  if (overMin < 60) return `Overdue by ${overMin}m`;
                  const h = Math.floor(overMin / 60); const m = overMin % 60;
                  return m === 0 ? `Overdue by ${h}h` : `Overdue by ${h}h ${m}m`;
                }
                const remainMs = SLA_HOURS * 3_600_000 - elapsedMs;
                const remainMin = Math.round(remainMs / 60000);
                if (remainMin < 60) return `Next update due in ${remainMin}m`;
                const h = Math.floor(remainMin / 60); const m = remainMin % 60;
                return m === 0 ? `Next update due in ${h}h` : `Next update due in ${h}h ${m}m`;
              };

              return (
                <Card mb={3} borderWidth="1px" borderColor={`${slaColor}.300`} shadow="sm">
                  {/* SLA banner — color screams when overdue */}
                  <Box bg={`${slaColor}.500`} color="white" px={4} py={1.5}>
                    <HStack justify="space-between" align="center" flexWrap="wrap">
                      <HStack spacing={2}>
                        <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="0.05em">
                          {overdue ? 'Update Overdue' : dueSoon ? 'Update Due Soon' : 'On Track'}
                        </Text>
                        <Text fontSize="xs" opacity={0.9}>· {SLA_HOURS}h update cadence</Text>
                      </HStack>
                      <Text fontSize="xs" fontWeight="700">{fmtRemaining()}</Text>
                    </HStack>
                  </Box>

                  <CardBody py={4}>
                    {/* Clickable hero — the whole block opens the drawer so
                        admin doesn't have to aim at a tiny button. */}
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => setStatusDrawerOpen(true)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setStatusDrawerOpen(true); }}
                      cursor="pointer"
                      borderRadius="md"
                      p={3}
                      mx={-3}
                      _hover={{ bg: 'gray.50' }}
                      transition="background 0.15s"
                    >
                      <HStack justify="space-between" align="start" flexWrap="wrap" gap={3}>
                        <VStack align="start" spacing={2} flex={1} minW="220px">
                          <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.06em">
                            Current Status
                          </Text>
                          <HStack spacing={2} flexWrap="wrap">
                            <Badge colorScheme={getSubmissionStatusColor(submission.status)} fontSize="md" px={3} py={1.5} borderRadius="md">
                              {getSubmissionStatusLabel(submission.status)}
                            </Badge>
                            {cfgNow && (
                              <Badge colorScheme={cfgNow.color} variant="solid" fontSize="md" px={3} py={1.5} borderRadius="md">
                                {cfgNow.label}
                              </Badge>
                            )}
                            {(() => {
                              const f = (latest?.outcome_flags?.length ? latest.outcome_flags : submission.outcome_flags) || [];
                              return f.map((k) => (
                                <Badge key={k} colorScheme="purple" variant="subtle" fontSize="sm" px={2} py={1} borderRadius="md">
                                  {getOutcomeFlagLabel(k)}
                                </Badge>
                              ));
                            })()}
                          </HStack>
                          <HStack spacing={3} fontSize="xs" color="gray.500">
                            <Text>Last updated <Text as="span" fontWeight="600" color={overdue ? 'red.600' : 'gray.700'}>{fmtElapsed(elapsedMs)}</Text></Text>
                            {latest && (
                              <Badge fontSize="2xs" colorScheme={latest.logged_by_kind === 'admin' ? 'gray' : latest.logged_by_kind === 'lab' ? 'purple' : 'blue'} variant="subtle">
                                by {resolveActorLabel(latest)}
                              </Badge>
                            )}
                          </HStack>
                          {(latest?.outcome_notes || fallbackNote) && (
                            <Box bg="gray.50" borderLeft="3px solid" borderColor={`${cfgNow?.color || 'gray'}.300`} px={3} py={2} borderRadius="sm" w="full">
                              <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap">"{latest?.outcome_notes || fallbackNote}"</Text>
                            </Box>
                          )}
                        </VStack>
                        <Button
                          colorScheme={overdue ? 'red' : 'teal'}
                          size="lg"
                          onClick={(e) => { e.stopPropagation(); setStatusDrawerOpen(true); }}
                          minW="180px"
                        >
                          {overdue ? '⚠ Add update now' : 'Add note / Update status'}
                        </Button>
                      </HStack>
                    </Box>

                    {/* Full activity timeline — everything, most recent first.
                        The rail scrolls, so no collapse needed. The hero block
                        above already shows the latest; this lists the rest. */}
                    {historyEvents.length > 0 && (
                      <>
                        <Divider my={3} />
                        <Text fontSize="2xs" color="gray.500" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" mb={2}>
                          Activity timeline
                        </Text>
                        <VStack align="stretch" spacing={3} divider={<Divider />}>
                          {historyEvents.map((ev) => {
                            const cfg = ev.outcome_code ? getOutcomeConfig(ev.outcome_code) : null;
                            const when = ev.logged_at
                              ? new Date(ev.logged_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
                              : '—';
                            const scheduled = formatSchedule(ev) || null;
                            return (
                              <Box key={ev.id}>
                                <HStack spacing={2} mb={1} flexWrap="wrap">
                                  {cfg && (
                                    <Badge colorScheme={cfg.color} variant="solid" borderRadius="full" px={2}>
                                      {cfg.label}
                                    </Badge>
                                  )}
                                  <Text fontSize="xs" color="gray.500">{when}</Text>
                                  <Badge fontSize="2xs" colorScheme={ev.logged_by_kind === 'admin' ? 'gray' : ev.logged_by_kind === 'lab' ? 'purple' : 'blue'} variant="subtle">
                                    {resolveActorLabel(ev)}
                                  </Badge>
                                  {(ev.outcome_flags || []).map((k) => (
                                    <Badge key={k} colorScheme="purple" variant="subtle" borderRadius="full" px={2} fontSize="2xs">
                                      {getOutcomeFlagLabel(k)}
                                    </Badge>
                                  ))}
                                </HStack>
                                {scheduled && (
                                  <Text fontSize="xs" color="gray.600" mb={1}>
                                    Availability: <Text as="span" fontWeight="600">{scheduled}</Text>
                                  </Text>
                                )}
                                {ev.outcome_notes && (
                                  <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap" mt={1}>
                                    "{ev.outcome_notes}"
                                  </Text>
                                )}
                              </Box>
                            );
                          })}
                        </VStack>
                      </>
                    )}

                    {statusEventsLoading && (
                      <HStack mt={3}><Spinner size="sm" /><Text fontSize="sm" color="gray.500">Loading history…</Text></HStack>
                    )}
                  </CardBody>
                </Card>
              );
            })()}
          </Box>

          {/* ── Right: editable submission sections ──────────────────────── */}
          <Box flex={1} minW={0}>
            {/* Phlebotomist banner — compact single-row strip. Editable phleb
                assignment, draw-fee toggle, and tags inline. */}
            <Card bg="teal.50" border="1px solid" borderColor="teal.100" mb={3}>
              <CardBody py={2} px={3}>
                <Flex align="center" gap={3} flexWrap="wrap">
                  {/* Phlebotomist (+ email underneath when viewing) */}
                  <Box minW="220px" flexShrink={0}>
                    <Text fontSize="2xs" color="teal.700" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em">Phlebotomist</Text>
                    {isEditing ? (
                      <Box position="relative">
                        <Input
                          size="sm"
                          bg="white"
                          // Input is PURELY a search field — never the picked
                          // value. The picked phleb shows as a chip below so
                          // typing in the input doesn't have to fight a stale
                          // pre-filled name.
                          value={phlebSearch}
                          placeholder={editForm.phlebotomist_id ? 'Search to change phleb…' : 'Search phlebs by name or email…'}
                          onFocus={() => setShowPhlebDropdown(true)}
                          onBlur={() => setTimeout(() => setShowPhlebDropdown(false), 200)}
                          onChange={(e) => {
                            setPhlebSearch(e.target.value);
                            setShowPhlebDropdown(true);
                          }}
                        />
                        {/* Picked-phleb indicator with one-click clear. */}
                        {editForm.phlebotomist_id && (
                          <HStack mt={1} spacing={2} fontSize="xs">
                            <Badge colorScheme="teal" variant="subtle">
                              {editForm.phlebotomist_name || 'Selected'}
                            </Badge>
                            <Text
                              as="button"
                              color="red.500"
                              fontWeight="600"
                              onClick={() => { setEditPhleb(''); setPhlebSearch(''); }}
                              _hover={{ textDecoration: 'underline' }}
                            >
                              Clear
                            </Text>
                          </HStack>
                        )}
                        {showPhlebDropdown && (
                          <Box
                            position="absolute" zIndex={20} w="100%" maxH="260px" overflowY="auto"
                            bg="white" border="1px solid" borderColor="gray.300" borderRadius="md"
                            mt={1} boxShadow="md" onMouseDown={(e) => e.preventDefault()}
                          >
                            {(() => {
                              const q = (phlebSearch || '').toLowerCase().trim();
                              const matches = phlebList.filter((p) => {
                                if (!q) return true;
                                const hay = [p.full_name, p.company_name, p.email].filter(Boolean).join(' ').toLowerCase();
                                return hay.includes(q);
                              }).slice(0, 30);
                              if (matches.length === 0) {
                                return <Box p={3}><Text fontSize="sm" color="gray.500">No phlebs match "{phlebSearch}"</Text></Box>;
                              }
                              return matches.map((p) => {
                                const isCurrent = editForm.phlebotomist_id === p.id;
                                return (
                                  <Box
                                    key={p.id} p={2} borderBottomWidth={1} cursor="pointer"
                                    bg={isCurrent ? 'teal.50' : 'white'}
                                    _hover={{ bg: 'teal.100' }}
                                    onClick={() => {
                                      setEditPhleb(p.id);
                                      setPhlebSearch('');
                                      setShowPhlebDropdown(false);
                                    }}
                                  >
                                    <HStack justify="space-between">
                                      <Box>
                                        <Text fontWeight="medium" fontSize="sm">
                                          {p.full_name || p.company_name || p.email || p.id}
                                        </Text>
                                        {p.email && (
                                          <Text fontSize="xs" color="gray.500">
                                            {p.email}
                                            {p.lab_draw_fee != null ? ` · $${Number(p.lab_draw_fee).toFixed(2)}/draw` : ''}
                                          </Text>
                                        )}
                                      </Box>
                                      {isCurrent && <Badge colorScheme="teal" fontSize="xx-small">current</Badge>}
                                    </HStack>
                                  </Box>
                                );
                              });
                            })()}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <>
                        <Text fontSize="sm" fontWeight="600" color={submission.phlebotomist_id ? 'gray.800' : 'red.500'}>
                          {resolvePhlebName(submission) || (submission.phlebotomist_id ? 'Unknown' : 'Unassigned')}
                        </Text>
                        {submission.phlebotomist_email && (
                          <Text fontSize="xs" color="gray.500" noOfLines={1}>{submission.phlebotomist_email}</Text>
                        )}
                      </>
                    )}
                  </Box>

                  <Divider orientation="vertical" h="28px" display={{ base: 'none', md: 'block' }} />

                  {/* Draw fee toggle */}
                  <HStack spacing={2} flexShrink={0}>
                    <Text fontSize="2xs" color="teal.700" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em">Draw fee</Text>
                    <Checkbox
                      isChecked={!!submission.draw_fee_paid}
                      colorScheme="green"
                      onChange={async (e) => {
                        const newVal = e.target.checked;
                        try {
                          const { error } = await supabase
                            .from('submissions')
                            .update({ draw_fee_paid: newVal })
                            .eq('id', submission.id);
                          if (error) throw error;
                          setSubmission(prev => ({ ...prev, draw_fee_paid: newVal }));
                          toast({
                            title: newVal ? 'Draw fee marked as paid' : 'Draw fee marked as unpaid',
                            status: 'success',
                            duration: 2000,
                          });
                        } catch (err) {
                          toast({ title: 'Error updating draw fee status', description: err.message, status: 'error', duration: 3000 });
                        }
                      }}
                    >
                      <Text fontSize="sm" color={submission.draw_fee_paid ? 'green.600' : 'gray.600'} fontWeight={submission.draw_fee_paid ? 'bold' : 'normal'}>
                        {submission.draw_fee_paid ? 'Paid' : 'Not paid'}
                      </Text>
                    </Checkbox>
                  </HStack>

                  <Divider orientation="vertical" h="28px" display={{ base: 'none', md: 'block' }} />

                  {/* Team — mutually-exclusive AARON / ANDALIS assignment.
                      Writes through submissions.tags by adding the chosen value
                      and stripping the other one, so the spreadsheet view's
                      Tag column reflects the assignment immediately. */}
                  {(() => {
                    const currentTags = Array.isArray(submission.tags) ? submission.tags : [];
                    const currentTeam = TEAM_TAGS.find((t) => currentTags.includes(t)) || '';
                    const setTeam = async (next) => {
                      // Drop every team tag, then add the picked one (if any).
                      const without = currentTags.filter((t) => !TEAM_TAGS.includes(t));
                      const nextTags = next ? [...without, next] : without;
                      setSubmission((prev) => (prev ? { ...prev, tags: nextTags } : prev));
                      const { error } = await supabase
                        .from('submissions')
                        .update({ tags: nextTags })
                        .eq('id', submission.id);
                      if (error) {
                        toast({ title: 'Could not update team', description: error.message, status: 'error', duration: 4000 });
                      }
                    };
                    return (
                      <HStack spacing={2} align="center">
                        <Text fontSize="2xs" color="teal.700" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" flexShrink={0}>Team</Text>
                        <Select
                          size="sm"
                          value={currentTeam}
                          onChange={(e) => setTeam(e.target.value)}
                          maxW="140px"
                          placeholder="— None —"
                          bg="white"
                        >
                          {TEAM_TAGS.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </Select>
                      </HStack>
                    );
                  })()}

                  <Divider orientation="vertical" h="28px" display={{ base: 'none', md: 'block' }} />

                  {/* Tags — grow to fill the rest of the row */}
                  <HStack spacing={2} flex={1} minW="200px" align="center">
                    <Text fontSize="2xs" color="teal.700" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" flexShrink={0}>Tags</Text>
                    <Box flex={1}>
                      <TagEditor
                        submissionId={submission.id}
                        value={Array.isArray(submission.tags) ? submission.tags : []}
                        onChange={(next) => setSubmission((prev) => (prev ? { ...prev, tags: next } : prev))}
                      />
                    </Box>
                  </HStack>
                </Flex>
              </CardBody>
            </Card>

            {/* Tabs/stepper removed — every section is laid out below so admins
                can see and edit all submission info at a glance. */}
            <Box mb={3}>
              <HStack justify="space-between" mb={1}>
                <Text fontSize="xs" fontWeight="medium" color="gray.600">
                  {completedCount} of {tabOrder.length} sections complete
                </Text>
                <Text fontSize="sm" fontWeight="bold" color="green.600">{progressPercent}%</Text>
              </HStack>
              <Progress value={progressPercent} size="sm" colorScheme="green" borderRadius="md" />
            </Box>

            {/* ── Three-panel body: middle = doctor script, right = logistics
                (drop-off/pickup on top) then the rest. Left rail (status/notes)
                sits outside this Flex. Stacks to one column on small screens
                and while editing so forms get full width. */}
            <Flex gap={3} align="flex-start" direction={{ base: "column", xl: isEditing ? "column" : "row" }}>
              {/* Middle panel — the doctor's script / order */}
              <Box flex={1} minW={0} w="100%">
                {/* Script Tab */}
                  <VStack spacing={4} align="stretch">
                    <Card variant="outline" borderColor="gray.200" borderRadius="lg" shadow="xs">
                      <CardHeader bg="blue.50" py={1.5}>
                        <Heading size="sm" color="blue.700">Doctor Script</Heading>
                      </CardHeader>
                      <CardBody py={3}>
                        {isEditing ? (
                          <VStack spacing={4} align="stretch">
                            <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                              <FormControl>
                                <FormLabel>Collection Time</FormLabel>
                                <Input
                                  type="datetime-local"
                                  value={(() => {
                                    const v = editForm.blood_collection_time || '';
                                    if (!v) return '';
                                    const d = new Date(v);
                                    if (isNaN(d.getTime()) || d.getFullYear() > 2099 || d.getFullYear() < 1900) return '';
                                    return v.slice(0, 16);
                                  })()}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val) {
                                      const yearMatch = val.match(/^(\d{4})/);
                                      if (yearMatch && (parseInt(yearMatch[1], 10) > 2099 || parseInt(yearMatch[1], 10) < 1900)) return;
                                    }
                                    // Editing the exact collection time here overrides any
                                    // availability window — flip the row back to 'exact' and
                                    // clear the window fields so the display stays consistent.
                                    setEditForm({
                                      ...editForm,
                                      blood_collection_time: val,
                                      scheduling_type: 'exact',
                                      availability_start_date: null,
                                      availability_end_date: null,
                                      availability_start_time: null,
                                      availability_end_time: null,
                                    });
                                  }}
                                  min="1900-01-01T00:00"
                                  max="2099-12-31T23:59"
                                  size="lg"
                                />
                              </FormControl>
                              <FormControl>
                                <FormLabel>Diagnosis</FormLabel>
                                <Input
                                  value={editForm.diagnosis || ''}
                                  onChange={(e) => setEditForm({ ...editForm, diagnosis: e.target.value })}
                                  placeholder="Enter diagnosis or ICD code..."
                                />
                              </FormControl>
                            </SimpleGrid>
                            <FormControl>
                              <FormLabel>Specimen</FormLabel>
                              {renderSpecimenEditor()}
                            </FormControl>
                            
                            <Box>
                              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>Doctor Script Document</Text>
                              <HStack spacing={3} mb={3} flexWrap="wrap">
                                {(() => {
                                  const urls = parseImageUrls(submission.script_image);
                                  return urls.length > 0 ? urls.map((url, idx) => (
                                    <Box key={idx} textAlign="center">
                                      <a href={url} target="_blank" rel="noopener noreferrer">
                                        {isImageFile(url) ? (
                                          <img src={url} alt={`Script ${idx + 1}`} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, marginBottom: 4, border: '1px solid #ccc' }} />
                                        ) : url?.endsWith('.pdf') ? <PdfIcon /> : null}
                                      </a>
                                      <Button as="a" href={url} target="_blank" size="xs" colorScheme="blue" leftIcon={<AttachmentIcon />}>
                                        View {urls.length > 1 ? idx + 1 : ''}
                                      </Button>
                                    </Box>
                                  )) : <Text fontSize="sm" color="gray.400">No document uploaded</Text>;
                                })()}
                              </HStack>
                              <input type="file" ref={scriptFileRef} hidden accept="image/*,.pdf" multiple onChange={(e) => { if (e.target.files.length > 0) handleDocUpload(Array.from(e.target.files), 'script_image'); e.target.value = ''; }} />
                              <Button size="sm" leftIcon={<AttachmentIcon />} colorScheme="blue" variant="outline" isLoading={docUploading.script} onClick={() => scriptFileRef.current?.click()}>
                                Upload Script
                              </Button>
                            </Box>

                            <FormControl>
                              <FormLabel fontSize="lg" fontWeight="bold" mb={3}>
                                Search Tests ({tests.length} available)
                              </FormLabel>
                              <Box position="relative">
                                <Input
                                  size="lg"
                                  h="60px"
                                  fontSize="lg"
                                  py={4}
                                  px={5}
                                  borderRadius="md"
                                  borderWidth="2px"
                                  value={testSearchQuery}
                                  onChange={(e) => {
                                    setTestSearchQuery(e.target.value);
                                    setShowTestDropdown(true);
                                  }}
                                  onFocus={() => setShowTestDropdown(true)}
                                  placeholder="Search tests by name or description..."
                                />
                                {testsLoading && (
                                  <Box position="absolute" right="10px" top="50%" transform="translateY(-50%)">
                                    <Spinner size="sm" />
                                  </Box>
                                )}
                                {showTestDropdown && testSearchQuery && filteredTests.length > 0 && (
                                  <Box
                                    position="absolute"
                                    zIndex={10}
                                    w="100%"
                                    maxH="200px"
                                    overflowY="auto"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    borderRadius="md"
                                    mt={1}
                                    boxShadow="md"
                                  >
                                    {filteredTests.map((test) => (
                                      <Box
                                        key={test.id}
                                        p={3}
                                        cursor="pointer"
                                        _hover={{ bg: 'blue.50' }}
                                        onClick={() => addTest(test)}
                                        borderBottom="1px solid"
                                        borderColor="gray.200"
                                      >
                                        <Text fontWeight="medium">{test.name || test.id}{test.code ? ` (${test.code})` : ''}</Text>
                                        {test.description && (
                                          <Text fontSize="sm" color="gray.600">{test.description}</Text>
                                        )}
                                        {test.cash_price != null && test.cash_price > 0 && (
                                          <Text fontSize="xs" color="green.600" fontWeight="semibold" mt={1}>
                                            ${Number(test.cash_price).toFixed(2)}
                                          </Text>
                                        )}
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                                {showTestDropdown && testSearchQuery && filteredTests.length === 0 && (
                                  <Box
                                    position="absolute"
                                    zIndex={10}
                                    w="100%"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    borderRadius="md"
                                    mt={1}
                                    boxShadow="md"
                                    p={2}
                                  >
                                    <Text fontSize="sm" color="gray.500">No tests found</Text>
                                  </Box>
                                )}
                              </Box>
                            </FormControl>

                            {selectedTests.length > 0 && (
                              <Box>
                                <Text fontSize="sm" fontWeight="bold" mb={2}>Selected Tests ({selectedTests.length})</Text>
                                <Table variant="simple" size="sm">
                                  <Thead>
                                    <Tr bg="gray.100">
                                      <Th>Test Name</Th>
                                      <Th>Description</Th>
                                      <Th>Price</Th>
                                      <Th>Diagnosis Code Present</Th>
                                      <Th>Actions</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {selectedTests.map((test) => (
                                      <Tr key={test.id}>
                                        <Td fontWeight="medium">{test.name || test.id}</Td>
                                        <Td fontSize="sm" color="gray.600">{test.description || 'N/A'}</Td>
                                        <Td>
                                          {test.cash_price != null ? (
                                            <Text color="green.600" fontWeight="semibold">${Number(test.cash_price).toFixed(2)}</Text>
                                          ) : (
                                            <Text color="gray.400">N/A</Text>
                                          )}
                                        </Td>
                                        <Td>
                                          <Checkbox
                                            isChecked={diagnosisCodesConfirmed[test.id] || false}
                                            onChange={() => toggleDiagnosisCodeConfirmed(test.id)}
                                            colorScheme="green"
                                          >
                                            <Text fontSize="sm">Confirmed</Text>
                                          </Checkbox>
                                        </Td>
                                        <Td>
                                          <Button size="xs" colorScheme="red" onClick={() => removeTest(test.id)}>
                                            Remove
                                          </Button>
                                        </Td>
                                      </Tr>
                                    ))}
                                  </Tbody>
                                </Table>
                              </Box>
                            )}
                          </VStack>
                        ) : (
                          <VStack spacing={4} align="stretch">
                            <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                              <Box>
                                <Text fontSize="xs" fontWeight="medium" color="gray.600">
                                  {submission.scheduling_type && submission.scheduling_type !== 'exact' ? 'Availability' : 'Collection Time'}
                                </Text>
                                <Text>{formatSchedule(submission) || 'N/A'}</Text>
                              </Box>
                            </SimpleGrid>
                            <Box>
                              <Text fontSize="xs" fontWeight="medium" color="gray.600">Specimen</Text>
                              {submission.specimen_type ? (() => {
                                try {
                                  const specs = JSON.parse(submission.specimen_type);
                                  return Array.isArray(specs) && specs.length > 0
                                    ? specs.map((s, i) => <Text key={i}>{s.quantity}x {s.type}</Text>)
                                    : <Text>{submission.specimen_type}</Text>;
                                } catch { return <Text>{submission.specimen_type}</Text>; }
                              })() : <Text color="gray.400">N/A</Text>}
                            </Box>
                            <Box>
                              <Text fontSize="xs" fontWeight="medium" color="gray.600">Diagnosis</Text>
                              <Text>{submission.diagnosis || <Text as="span" color="gray.400">N/A</Text>}</Text>
                            </Box>

                            {/* Doctor Script (left) + the tests on it (right). */}
                            <Flex direction={{ base: 'column', lg: 'row' }} gap={4} align="stretch">
                              <Box w={{ base: '100%', lg: '240px' }} flexShrink={0}>
                                <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>Doctor Script</Text>
                                <HStack spacing={3} mb={3} flexWrap="wrap">
                                  {(() => {
                                    const urls = parseImageUrls(submission.script_image);
                                    return urls.length > 0 ? urls.map((url, idx) => (
                                      <Box key={idx} textAlign="center">
                                        <a href={url} target="_blank" rel="noopener noreferrer">
                                          {isImageFile(url) ? (
                                            <img src={url} alt={`Script ${idx + 1}`} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, marginBottom: 4, border: '1px solid #ccc' }} />
                                          ) : url?.endsWith('.pdf') ? <PdfIcon /> : null}
                                        </a>
                                        <Button as="a" href={url} target="_blank" size="xs" colorScheme="blue" leftIcon={<AttachmentIcon />}>
                                          View {urls.length > 1 ? idx + 1 : ''}
                                        </Button>
                                      </Box>
                                    )) : <Text fontSize="sm" color="gray.400">No document uploaded</Text>;
                                  })()}
                                </HStack>
                                <input type="file" ref={scriptFileRef} hidden accept="image/*,.pdf" multiple onChange={(e) => { if (e.target.files.length > 0) handleDocUpload(Array.from(e.target.files), 'script_image'); e.target.value = ''; }} />
                                <Button size="sm" leftIcon={<AttachmentIcon />} colorScheme="blue" variant="outline" isLoading={docUploading.script} onClick={() => scriptFileRef.current?.click()}>
                                  Upload Script
                                </Button>
                              </Box>

                              {/* Tests on the script — just the names; the tube guide
                                  below carries the collection detail. */}
                              {selectedTests.length > 0 && (
                                <Box flex={1} minW={0}>
                                  <HStack mb={2} spacing={2}>
                                    <Text fontSize="sm" fontWeight="bold">Tests on the Script ({selectedTests.length})</Text>
                                    {submission?.tube_guide_confirmed_at && (
                                      <Badge colorScheme="green" variant="subtle">
                                        ✓ Tubes confirmed by phleb · {new Date(submission.tube_guide_confirmed_at).toLocaleString()}
                                      </Badge>
                                    )}
                                  </HStack>
                                  <VStack
                                    align="stretch"
                                    spacing={0}
                                    border="1px solid"
                                    borderColor="gray.100"
                                    borderRadius="md"
                                    overflow="hidden"
                                  >
                                    {selectedTests.map((test, i) => (
                                      <HStack
                                        key={test.id}
                                        spacing={2}
                                        px={3}
                                        py={2}
                                        bg={i % 2 === 0 ? 'white' : 'gray.50'}
                                      >
                                        <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                          {test.name || test.id}
                                        </Text>
                                        {test.code && (
                                          <Text fontSize="xs" color="gray.400">({test.code})</Text>
                                        )}
                                      </HStack>
                                    ))}
                                  </VStack>

                                  {/* Test Tube Guide — the same structured draw
                                      guide the phleb sees, read-only here, with each
                                      tube's collection progress (who / when). */}
                                  <Box mt={4}>
                                    <TubeGuide
                                      result={tubeGuide}
                                      progress={submission?.tube_guide_progress || {}}
                                      readOnly
                                    />
                                  </Box>
                                </Box>
                              )}
                            </Flex>
                          </VStack>
                        )}
                      </CardBody>
                    </Card>
                  </VStack>

                {/* Phlebotomist tab removed - info shown in read-only banner above */}

                

              </Box>

              {/* Right side — drop-off/pickup on top, then everything else */}
              <VStack flex={1} minW={0} w="100%" align="stretch" spacing={3}>
                {/* Drop-off / Pickup logistics — replaces FedEx shipping. The
                    phleb either drops the sample at a managed location or has it
                    picked up from their place (with the pickup address). */}
                  <Card variant="outline" borderColor="gray.200" borderRadius="lg" shadow="xs">
                    <CardHeader bg="purple.50" py={1.5}>
                      <HStack justify="space-between">
                        <Heading size="sm" color="purple.700">Drop-off / Pickup</Heading>
                        {(submission.dropped_off_at || submission.picked_up_at) && (
                          <Badge colorScheme="green">
                            {submission.dropped_off_at ? 'Dropped off' : 'Picked up'}
                          </Badge>
                        )}
                      </HStack>
                    </CardHeader>
                    <CardBody py={3}>
                      <VStack align="stretch" spacing={4}>
                        {/* Phleb-captured proof: the spun specimen photo and the
                            proof-of-drop photo (showing the drop box). */}
                        {(submission.spin_photo_url || submission.drop_photo_url) && (
                          <Box>
                            <FormLabel mb={2}>Proof photos</FormLabel>
                            <HStack spacing={4} align="flex-start" flexWrap="wrap">
                              {submission.spin_photo_url && (
                                <Box textAlign="center">
                                  <a href={submission.spin_photo_url} target="_blank" rel="noopener noreferrer">
                                    <img src={submission.spin_photo_url} alt="Spun specimen" style={{ width: 130, height: 130, objectFit: 'cover', borderRadius: 6, marginBottom: 4, border: '1px solid #ccc' }} />
                                  </a>
                                  <Text fontSize="xs" color="gray.500">Spun specimen</Text>
                                </Box>
                              )}
                              {submission.drop_photo_url && (
                                <Box textAlign="center">
                                  <a href={submission.drop_photo_url} target="_blank" rel="noopener noreferrer">
                                    <img src={submission.drop_photo_url} alt="Proof of drop" style={{ width: 130, height: 130, objectFit: 'cover', borderRadius: 6, marginBottom: 4, border: '1px solid #2f855a' }} />
                                  </a>
                                  <Text fontSize="xs" color="green.600" fontWeight="600">Drop-off proof</Text>
                                </Box>
                              )}
                            </HStack>
                          </Box>
                        )}
                        <FormControl>
                          <FormLabel>Handoff method</FormLabel>
                          <Select
                            value={submission.handoff_method || ''}
                            isDisabled={handoffSaving}
                            onChange={(e) => updateHandoff({ handoff_method: e.target.value || null })}
                          >
                            <option value="">Select method…</option>
                            <option value="dropoff">Drop off at a location</option>
                            <option value="pickup">Picked up from phlebotomist</option>
                          </Select>
                        </FormControl>

                        {submission.handoff_method === 'dropoff' && (
                          <>
                            <FormControl>
                              <FormLabel>Drop-off location</FormLabel>
                              <Select
                                placeholder="Select drop-off location…"
                                value={submission.drop_off_location_id || ''}
                                isDisabled={handoffSaving}
                                onChange={(e) => updateHandoff({ drop_off_location_id: e.target.value || null })}
                              >
                                {dropOffLocations.map((loc) => (
                                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                                ))}
                              </Select>
                              {(() => {
                                const loc = dropOffLocations.find((l) => l.id === submission.drop_off_location_id);
                                return loc?.address ? (
                                  <Text fontSize="sm" color="gray.500" mt={1}>{loc.address}</Text>
                                ) : null;
                              })()}
                              {dropOffLocations.length === 0 && (
                                <Text fontSize="sm" color="orange.500" mt={1}>
                                  No drop-off locations configured yet — add them under Admin → Drop-off Locations.
                                </Text>
                              )}
                            </FormControl>

                            {submission.dropped_off_at ? (
                              <HStack justify="space-between">
                                <Text fontSize="sm" color="green.600">
                                  Dropped off {new Date(submission.dropped_off_at).toLocaleString()}
                                </Text>
                                <Button size="sm" variant="ghost" colorScheme="red" isDisabled={handoffSaving}
                                  onClick={() => updateHandoff({ dropped_off_at: null })}>
                                  Undo
                                </Button>
                              </HStack>
                            ) : (
                              <Button colorScheme="purple" alignSelf="start" isLoading={handoffSaving}
                                isDisabled={!submission.drop_off_location_id}
                                onClick={() => updateHandoff({ dropped_off_at: new Date().toISOString(), status: 'completed' })}>
                                Mark dropped off
                              </Button>
                            )}
                          </>
                        )}

                        {submission.handoff_method === 'pickup' && (
                          <>
                            <FormControl>
                              <FormLabel>Pickup location (phlebotomist address)</FormLabel>
                              <Input
                                defaultValue={submission.pickup_location || ''}
                                placeholder="Street, City, State ZIP"
                                isDisabled={handoffSaving}
                                onBlur={(e) => {
                                  const v = e.target.value.trim();
                                  if (v !== (submission.pickup_location || '')) updateHandoff({ pickup_location: v || null });
                                }}
                              />
                            </FormControl>

                            {submission.picked_up_at ? (
                              <HStack justify="space-between">
                                <Text fontSize="sm" color="green.600">
                                  Picked up {new Date(submission.picked_up_at).toLocaleString()}
                                </Text>
                                <Button size="sm" variant="ghost" colorScheme="red" isDisabled={handoffSaving}
                                  onClick={() => updateHandoff({ picked_up_at: null })}>
                                  Undo
                                </Button>
                              </HStack>
                            ) : (
                              <Button colorScheme="purple" alignSelf="start" isLoading={handoffSaving}
                                isDisabled={!submission.pickup_location}
                                onClick={() => updateHandoff({ picked_up_at: new Date().toISOString(), status: 'completed' })}>
                                Mark picked up
                              </Button>
                            )}
                          </>
                        )}
                      </VStack>
                    </CardBody>
                  </Card>

                {/* Manual orders: admin enters the Order ID here. Lab-API orders
                    already carry one (shown read-only in Lab Order Details below). */}
                {!submission.ldx_message_id && (
                  <Card variant="outline" borderColor="gray.200" borderRadius="lg" shadow="xs">
                    <CardHeader bg="blue.50" py={1.5}>
                      <HStack justify="space-between">
                        <Heading size="sm" color="blue.700">Order Details</Heading>
                        <Badge colorScheme="blue">Manual order</Badge>
                      </HStack>
                    </CardHeader>
                    <CardBody py={3}>
                      <FormControl maxW="340px">
                        <FormLabel fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>Order ID</FormLabel>
                        <Input
                          size="sm"
                          placeholder="Enter the lab order ID"
                          defaultValue={submission.lab_order_number || ''}
                          onBlur={(e) => saveOrderId(e.target.value)}
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Shown to the phlebotomist as “Order #…”, same as lab-API orders.
                        </Text>
                      </FormControl>
                    </CardBody>
                  </Card>
                )}

                {/* Lab Order Details — only for orders pulled from the lab API
                    (LDX). Read-only snapshot of the fields that came over the
                    wire, so nothing from the order is hidden. */}
                {submission.ldx_message_id && (() => {
                  const rows = [
                    ['Lab order #', submission.lab_order_number],
                    ['Order date', submission.order_date
                      ? new Date(submission.order_date).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
                      : null],
                    ['Client code', submission.client_code],
                    ['Visit #', submission.visit_number],
                    ['Patient MRN', submission.patient_mrn],
                    ['Ordering provider NPI', submission.doctor_npi],
                    ['Diagnoses (ICD-10)', submission.diagnosis],
                    ['Insurance group #', submission.group_number],
                    ['Insurance company address', submission.insurance_company_address],
                    ['Guarantor', [submission.guarantor_name, submission.guarantor_relationship && `(${submission.guarantor_relationship})`].filter(Boolean).join(' ')],
                    ['Guarantor DOB', submission.guarantor_dob],
                    ['Guarantor phone', submission.guarantor_phone],
                    ['Guarantor address', submission.guarantor_address],
                  ]
                    // Drop blanks and HL7 empty-component junk (e.g. "^^" / "^^^^"),
                    // which is only caret/separator characters — not a real value.
                    .filter(([, v]) => v && String(v).replace(/[\s^~&|,]/g, '') !== '');
                  return (
                    <Card variant="outline" borderColor="gray.200" borderRadius="lg" shadow="xs">
                      <CardHeader bg="purple.50" py={1.5}>
                        <HStack justify="space-between">
                          <Heading size="sm" color="purple.700">Lab Order Details</Heading>
                          <Badge colorScheme="purple">From lab API</Badge>
                        </HStack>
                      </CardHeader>
                      <CardBody py={3}>
                        {rows.length === 0 ? (
                          <Text color="gray.500" fontSize="sm">No additional order fields were provided.</Text>
                        ) : (
                          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                            {rows.map(([label, value]) => (
                              <Box key={label}>
                                <Text fontSize="xs" fontWeight="medium" color="gray.600">{label}</Text>
                                <Text>{value}</Text>
                              </Box>
                            ))}
                          </SimpleGrid>
                        )}
                      </CardBody>
                    </Card>
                  );
                })()}

                {/* Patient Info Tab */}
                  <Card variant="outline" borderColor="gray.200" borderRadius="lg" shadow="xs">
                    <CardHeader bg="blue.50" py={1.5}>
                      <Heading size="sm" color="blue.700">Patient Information</Heading>
                    </CardHeader>
                    <CardBody py={3}>
                      {isEditing ? (
                        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                          <FormControl>
                            <FormLabel>Name</FormLabel>
                            <Input value={editForm.patient_name || ''} onChange={(e) => setEditForm({ ...editForm, patient_name: e.target.value })} />
                          </FormControl>
                          <FormControl>
                            <FormLabel>DOB</FormLabel>
                            <Input type="date" value={editForm.patient_dob || ''} onChange={(e) => setEditForm({ ...editForm, patient_dob: e.target.value })} />
                          </FormControl>
                          <FormControl>
                            <FormLabel>Gender</FormLabel>
                            <Select value={editForm.patient_gender || ''} onChange={(e) => setEditForm({ ...editForm, patient_gender: e.target.value })}>
                              <option value="">Select</option>
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                            </Select>
                          </FormControl>
                          <FormControl>
                            <FormLabel>Email</FormLabel>
                            <Input value={editForm.patient_email || ''} onChange={(e) => setEditForm({ ...editForm, patient_email: e.target.value })} />
                          </FormControl>
                          <FormControl>
                            <FormLabel>Client Name</FormLabel>
                            <Input value={editForm.client_name || ''} onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })} />
                          </FormControl>
                          <FormControl>
                            <FormLabel>Street</FormLabel>
                            <Input value={editForm.patient_street || ''} onChange={(e) => setEditForm({ ...editForm, patient_street: e.target.value })} />
                          </FormControl>
                          <FormControl>
                            <FormLabel>City</FormLabel>
                            <Input value={editForm.patient_city || ''} onChange={(e) => setEditForm({ ...editForm, patient_city: e.target.value })} />
                          </FormControl>
                          <FormControl>
                            <FormLabel>State</FormLabel>
                            <Input value={editForm.patient_state || ''} onChange={(e) => setEditForm({ ...editForm, patient_state: e.target.value })} />
                          </FormControl>
                          <FormControl>
                            <FormLabel>ZIP</FormLabel>
                            <Input value={editForm.patient_zip || ''} onChange={(e) => setEditForm({ ...editForm, patient_zip: e.target.value })} />
                          </FormControl>
                          <FormControl>
                            <FormLabel>Phone</FormLabel>
                            <Input value={editForm.patient_phone || ''} onChange={(e) => setEditForm({ ...editForm, patient_phone: e.target.value })} />
                          </FormControl>
                        </SimpleGrid>
                      ) : (
                        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                          <Box>
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">Name</Text>
                            <Text>{(submission.patient_name && submission.patient_name.trim()) ? submission.patient_name : (fallbackPatientName || 'N/A')}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">DOB</Text>
                            <Text>{submission.patient_dob || 'N/A'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">Gender</Text>
                            <Text>{submission.patient_gender || 'N/A'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">Phone</Text>
                            {submission.patient_phone ? (
                              <HStack spacing={2}>
                                <Text>{submission.patient_phone}</Text>
                                <DialerrActions
                                  phone={submission.patient_phone}
                                  name={submission.patient_name}
                                />
                              </HStack>
                            ) : (
                              <Text color="gray.400">—</Text>
                            )}
                          </Box>
                          <Box>
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">Email</Text>
                            <Text>{submission.patient_email || '—'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">Client Name</Text>
                            <Text>{submission.client_name || '—'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">Street</Text>
                            <Text>{submission.patient_street || 'N/A'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">City</Text>
                            <Text>{submission.patient_city || 'N/A'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">State</Text>
                            <Text>{submission.patient_state || 'N/A'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">ZIP</Text>
                            <Text>{submission.patient_zip || 'N/A'}</Text>
                          </Box>
                          <Box gridColumn="1 / -1">
                            <Text fontSize="xs" color="gray.500">Address (combined)</Text>
                            <Text>{submission.patient_address || '—'}</Text>
                          </Box>
                          <Box gridColumn="1 / -1">
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">Patient ID</Text>
                            <Text fontWeight="bold" color="blue.700">{patientIdNumber || 'N/A'}</Text>
                          </Box>
                          <Box gridColumn="1 / -1">
                            <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>Patient ID Document</Text>
                            <HStack spacing={3} mb={3} flexWrap="wrap">
                              {(() => {
                                const urls = parseImageUrls(submission.patient_id_image);
                                return urls.length > 0 ? urls.map((url, idx) => (
                                  <Box key={idx} textAlign="center">
                                    <a href={url} target="_blank" rel="noopener noreferrer">
                                      {isImageFile(url) ? (
                                        <img src={url} alt={`Patient ID ${idx + 1}`} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, marginBottom: 4, border: '1px solid #ccc' }} />
                                      ) : url?.endsWith('.pdf') ? <PdfIcon /> : null}
                                    </a>
                                    <Button as="a" href={url} target="_blank" size="xs" colorScheme="orange" leftIcon={<AttachmentIcon />}>
                                      View {urls.length > 1 ? idx + 1 : ''}
                                    </Button>
                                  </Box>
                                )) : <Text fontSize="sm" color="gray.400">No document uploaded</Text>;
                              })()}
                            </HStack>
                            <input type="file" ref={patientIdFileRef} hidden accept="image/*,.pdf" multiple onChange={(e) => { if (e.target.files.length > 0) handleDocUpload(Array.from(e.target.files), 'patient_id_image'); e.target.value = ''; }} />
                            <Button size="sm" leftIcon={<AttachmentIcon />} colorScheme="blue" variant="outline" isLoading={docUploading.patientId} onClick={() => patientIdFileRef.current?.click()}>
                              Upload Patient ID
                            </Button>
                          </Box>
                        </SimpleGrid>
                      )}
                    </CardBody>
                  </Card>

                {/* Doctor Info Tab */}
                  <Card variant="outline" borderColor="gray.200" borderRadius="lg" shadow="xs">
                    <CardHeader bg="green.50" py={1.5}>
                      <Heading size="sm" color="green.700">Doctor Information</Heading>
                    </CardHeader>
                    <CardBody py={3}>
                      {isEditing ? (
                        <VStack spacing={4} align="stretch">
                          {/* Doctor search dropdown */}
                          <FormControl>
                            <FormLabel>Search Doctor (or keep current and edit below)</FormLabel>
                            <Box position="relative" ref={doctorSearchRef}>
                              <Input
                                value={doctorSearchQuery}
                                onChange={(e) => {
                                  setDoctorSearchQuery(e.target.value);
                                  setShowDoctorDropdown(true);
                                }}
                                onFocus={() => setShowDoctorDropdown(true)}
                                placeholder="Search by name, clinic, or email..."
                              />
                              {doctorsLoading && (
                                <Box position="absolute" right="10px" top="50%" transform="translateY(-50%)">
                                  <Spinner size="sm" />
                                </Box>
                              )}
                              {showDoctorDropdown && doctorSearchQuery && (
                                <Box
                                  position="absolute" zIndex={20} w="100%" maxH="200px" overflowY="auto"
                                  bg="white" border="1px solid" borderColor="gray.300" borderRadius="md" mt={1} boxShadow="md"
                                >
                                  {doctorsLoading ? (
                                    <Box p={3} textAlign="center"><Spinner size="sm" /> <Text as="span" fontSize="sm" color="gray.500" ml={2}>Loading doctors...</Text></Box>
                                  ) : filteredDoctors.length > 0 ? (
                                    filteredDoctors.map((doc) => (
                                      <Box
                                        key={doc.id} p={3} cursor="pointer"
                                        _hover={{ bg: 'green.50' }}
                                        onClick={() => selectDoctor(doc)}
                                        borderBottom="1px solid" borderColor="gray.100"
                                      >
                                        <Text fontWeight="medium">{doc.full_name}</Text>
                                        <HStack spacing={3} fontSize="xs" color="gray.500">
                                          {doc.clinic_name && <Text>{doc.clinic_name}</Text>}
                                          {doc.email && <Text>{doc.email}</Text>}
                                        </HStack>
                                      </Box>
                                    ))
                                  ) : (
                                    <Box p={3}><Text fontSize="sm" color="gray.500">No doctors found</Text></Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </FormControl>
                          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                            <FormControl>
                              <FormLabel>Name</FormLabel>
                              <Input value={editForm.doctor_name || ''} onChange={(e) => setEditForm({ ...editForm, doctor_name: e.target.value })} />
                            </FormControl>
                            <FormControl>
                              <FormLabel>Client Name</FormLabel>
                              <Input value={editForm.client_name || ''} onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })} />
                            </FormControl>
                            <FormControl>
                              <FormLabel>Phone</FormLabel>
                              <Input value={editForm.doctor_phone || ''} onChange={(e) => setEditForm({ ...editForm, doctor_phone: e.target.value })} />
                            </FormControl>
                            <FormControl>
                              <FormLabel>Email</FormLabel>
                              <Input value={editForm.doctor_email || ''} onChange={(e) => setEditForm({ ...editForm, doctor_email: e.target.value })} />
                            </FormControl>
                            <FormControl>
                              <FormLabel>Fax</FormLabel>
                              <Input value={editForm.doctor_fax || ''} onChange={(e) => setEditForm({ ...editForm, doctor_fax: e.target.value })} />
                            </FormControl>
                            <FormControl>
                              <FormLabel>Address</FormLabel>
                              <Input value={editForm.doctor_address || ''} onChange={(e) => setEditForm({ ...editForm, doctor_address: e.target.value })} />
                            </FormControl>
                          </SimpleGrid>
                        </VStack>
                      ) : (
                        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                          <Box>
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">Name</Text>
                            <Text>{submission.doctor_name || 'N/A'}</Text>
                          </Box>
                          {submission.client_name && (
                            <Box>
                              <Text fontSize="xs" fontWeight="medium" color="gray.600">Client Name</Text>
                              <Text>{submission.client_name}</Text>
                            </Box>
                          )}
                          <Box>
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">Phone</Text>
                            <Text>{submission.doctor_phone || 'N/A'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">Email</Text>
                            <Text>{submission.doctor_email || 'N/A'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">Fax</Text>
                            <Text>{submission.doctor_fax || 'N/A'}</Text>
                          </Box>
                          <Box gridColumn="1 / -1">
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">Address</Text>
                            <Text>{submission.doctor_address || 'N/A'}</Text>
                          </Box>
                        </SimpleGrid>
                      )}
                    </CardBody>
                  </Card>

                {/* Insurance Tab */}
                  <Card variant="outline" borderColor="gray.200" borderRadius="lg" shadow="xs">
                    <CardHeader bg={submission.is_self_pay ? 'green.50' : 'orange.50'}>
                      <HStack justify="space-between">
                        <Heading size="md" color={submission.is_self_pay ? 'green.700' : 'orange.700'}>
                          {submission.is_self_pay ? 'Self Pay' : 'Insurance'}
                        </Heading>
                        {submission.is_self_pay && (
                          <Badge
                            colorScheme={submission.payment_status === 'paid' ? 'green' : submission.payment_status === 'pending' ? 'yellow' : 'orange'}
                            fontSize="sm"
                            variant={submission.payment_status === 'paid' ? 'solid' : 'subtle'}
                          >
                            {submission.payment_status === 'paid' ? 'PAID' : submission.payment_status === 'pending' ? 'PAYMENT PENDING' : 'UNPAID'}
                          </Badge>
                        )}
                      </HStack>
                    </CardHeader>
                    <CardBody py={3}>
                      {submission.is_self_pay ? (
                        <VStack spacing={4} align="stretch">
                          <Box p={4} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                            <Text fontWeight="bold" color="green.700" mb={3}>Payment Details</Text>
                            {submission.self_pay_amount && (
                              <HStack justify="space-between" mb={2}>
                                <Text color="gray.600">Amount</Text>
                                <Text fontWeight="bold" fontSize="lg" color="green.700">${parseFloat(submission.self_pay_amount).toFixed(2)}</Text>
                              </HStack>
                            )}
                            <HStack justify="space-between" mb={2}>
                              <Text color="gray.600">Payment Status</Text>
                              <Badge
                                colorScheme={submission.payment_status === 'paid' ? 'green' : submission.payment_status === 'pending' ? 'yellow' : 'red'}
                                fontSize="sm"
                              >
                                {(submission.payment_status || 'unpaid').toUpperCase()}
                              </Badge>
                            </HStack>
                            {submission.stripe_payment_intent_id && (
                              <HStack justify="space-between">
                                <Text color="gray.600" fontSize="sm">Stripe Reference</Text>
                                <Text fontSize="xs" color="gray.500" fontFamily="mono">{submission.stripe_payment_intent_id}</Text>
                              </HStack>
                            )}
                          </Box>
                          {submission.payment_status !== 'paid' && (
                            <VStack spacing={3} align="stretch">
                              <Alert status="info" borderRadius="md">
                                <AlertIcon />
                                <Text fontSize="sm">Patient has not completed payment yet. Payment can be collected at the time of service or via the checkout link.</Text>
                              </Alert>
                              <Button
                                colorScheme="green"
                                size="sm"
                                onClick={handleGeneratePaymentLink}
                              >
                                Copy Payment Link
                              </Button>
                              {paymentLink && (
                                <Box p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                                  <Text fontSize="xs" color="gray.500" mb={1}>Payment Link (copied to clipboard):</Text>
                                  <HStack>
                                    <Input size="sm" value={paymentLink} isReadOnly fontSize="xs" />
                                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(paymentLink); toast({ title: 'Copied!', status: 'success', duration: 1500 }); }}>
                                      Copy
                                    </Button>
                                  </HStack>
                                </Box>
                              )}
                            </VStack>
                          )}
                        </VStack>
                      ) : isEditing ? (
                        <VStack spacing={4} align="stretch">
                          {/* Insurance search dropdown */}
                          <FormControl>
                            <FormLabel>Search Insurance (or keep current and edit below)</FormLabel>
                            <Box position="relative" ref={insuranceSearchRef}>
                              <Input
                                value={insuranceSearchQuery}
                                onChange={(e) => {
                                  setInsuranceSearchQuery(e.target.value);
                                  setShowInsuranceDropdown(true);
                                }}
                                onFocus={() => setShowInsuranceDropdown(true)}
                                placeholder="Search by insurance name or type..."
                              />
                              {insurancesLoading && (
                                <Box position="absolute" right="10px" top="50%" transform="translateY(-50%)">
                                  <Spinner size="sm" />
                                </Box>
                              )}
                              {showInsuranceDropdown && insuranceSearchQuery && (
                                <Box
                                  position="absolute" zIndex={20} w="100%" maxH="200px" overflowY="auto"
                                  bg="white" border="1px solid" borderColor="gray.300" borderRadius="md" mt={1} boxShadow="md"
                                >
                                  {insurancesLoading ? (
                                    <Box p={3} textAlign="center"><Spinner size="sm" /> <Text as="span" fontSize="sm" color="gray.500" ml={2}>Loading insurances...</Text></Box>
                                  ) : filteredInsurances.length > 0 ? (
                                    filteredInsurances.map((ins) => (
                                      <Box
                                        key={ins.id} p={3} cursor="pointer"
                                        _hover={{ bg: 'orange.50' }}
                                        onClick={() => selectInsurance(ins)}
                                        borderBottom="1px solid" borderColor="gray.100"
                                      >
                                        <Text fontWeight="medium">{ins.name}</Text>
                                        {ins.type && <Text fontSize="xs" color="gray.500">{ins.type}</Text>}
                                      </Box>
                                    ))
                                  ) : (
                                    <Box p={3}><Text fontSize="sm" color="gray.500">No insurances found</Text></Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </FormControl>
                          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                            <FormControl>
                              <FormLabel>Company</FormLabel>
                              <Input value={editForm.insurance_company || ''} onChange={(e) => setEditForm({ ...editForm, insurance_company: e.target.value })} />
                            </FormControl>
                            <FormControl>
                              <FormLabel>Policy Number</FormLabel>
                              <Input value={editForm.insurance_policy_number || ''} onChange={(e) => setEditForm({ ...editForm, insurance_policy_number: e.target.value })} />
                            </FormControl>
                          </SimpleGrid>
                          <Box>
                            <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>Insurance Card</Text>
                            <HStack spacing={3} mb={3} flexWrap="wrap">
                              {(() => {
                                const urls = parseImageUrls(submission.insurance_card_image);
                                return urls.length > 0 ? urls.map((url, idx) => (
                                  <Box key={idx} textAlign="center">
                                    <a href={url} target="_blank" rel="noopener noreferrer">
                                      {isImageFile(url) ? (
                                        <img src={url} alt={`Insurance Card ${idx + 1}`} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, marginBottom: 4, border: '1px solid #ccc' }} />
                                      ) : url?.endsWith('.pdf') ? <PdfIcon /> : null}
                                    </a>
                                    <Button as="a" href={url} target="_blank" size="xs" colorScheme="purple" leftIcon={<AttachmentIcon />}>
                                      View {urls.length > 1 ? idx + 1 : ''}
                                    </Button>
                                  </Box>
                                )) : <Text fontSize="sm" color="gray.400">No document uploaded</Text>;
                              })()}
                            </HStack>
                            <input type="file" ref={insuranceCardFileRef} hidden accept="image/*,.pdf" multiple onChange={(e) => { if (e.target.files.length > 0) handleDocUpload(Array.from(e.target.files), 'insurance_card_image'); e.target.value = ''; }} />
                            <Button size="sm" leftIcon={<AttachmentIcon />} colorScheme="orange" variant="outline" isLoading={docUploading.insurance} onClick={() => insuranceCardFileRef.current?.click()}>
                              Upload Insurance Card
                            </Button>
                          </Box>
                        </VStack>
                      ) : (
                        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                          <Box>
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">Company</Text>
                            <Text>{submission.insurance_company || 'N/A'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" fontWeight="medium" color="gray.600">Policy Number</Text>
                            <Text>{submission.insurance_policy_number || submission.policy_number || 'N/A'}</Text>
                          </Box>
                          <Box gridColumn="1 / -1">
                            <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>Insurance Card</Text>
                            <HStack spacing={3} mb={3} flexWrap="wrap">
                              {(() => {
                                const urls = parseImageUrls(submission.insurance_card_image);
                                return urls.length > 0 ? urls.map((url, idx) => (
                                  <Box key={idx} textAlign="center">
                                    <a href={url} target="_blank" rel="noopener noreferrer">
                                      {isImageFile(url) ? (
                                        <img src={url} alt={`Insurance Card ${idx + 1}`} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, marginBottom: 4, border: '1px solid #ccc' }} />
                                      ) : url?.endsWith('.pdf') ? <PdfIcon /> : null}
                                    </a>
                                    <Button as="a" href={url} target="_blank" size="xs" colorScheme="purple" leftIcon={<AttachmentIcon />}>
                                      View {urls.length > 1 ? idx + 1 : ''}
                                    </Button>
                                  </Box>
                                )) : <Text fontSize="sm" color="gray.400">No document uploaded</Text>;
                              })()}
                            </HStack>
                            <input type="file" ref={insuranceCardFileRef} hidden accept="image/*,.pdf" multiple onChange={(e) => { if (e.target.files.length > 0) handleDocUpload(Array.from(e.target.files), 'insurance_card_image'); e.target.value = ''; }} />
                            <Button size="sm" leftIcon={<AttachmentIcon />} colorScheme="orange" variant="outline" isLoading={docUploading.insurance} onClick={() => insuranceCardFileRef.current?.click()}>
                              Upload Insurance Card
                            </Button>
                          </Box>
                        </SimpleGrid>
                      )}
                    </CardBody>
                  </Card>

                {/* Available Labs — the lab "shopping" UI (header + options) is
                    only useful before a lab is locked in. Once the draw came
                    from the lab API (ldx_message_id) or was assigned to a lab
                    manually (lab_id), we hide the header + options and just show
                    the Assigned Lab card below. */}
                  <VStack align="stretch" spacing={3} mb={3}>
                    {!(submission.lab_id || submission.ldx_message_id) && (
                      <HStack justify="space-between">
                        <Text fontWeight="semibold">Available Labs</Text>
                      </HStack>
                    )}
                    {/* Show currently assigned lab if present */}
                    {submission.lab_id && (
                      <Card variant="outline" borderColor="gray.200" borderRadius="lg" shadow="xs">
                        <CardHeader bg="green.50" py={1.5}>
                          <HStack justify="space-between">
                            <Heading size="sm" color="green.700">Assigned Lab</Heading>
                            <HStack>
                              {selectedLabLoading && <Spinner size="sm" />}
                              <Button size="sm" variant="outline" colorScheme="red" isLoading={unassignLoading} onClick={async () => {
                                try {
                                  setUnassignLoading(true);
                                  const { error } = await supabase
                                    .from('submissions')
                                    .update({ lab_id: null })
                                    .eq('id', submission.id);
                                  if (error) throw error;
                                  setSubmission(prev => ({ ...prev, lab_id: null }));
                                  setSelectedLabDetails(null);
                                  toast({ title: 'Lab unassigned', status: 'success', duration: 1500, isClosable: true });
                                } catch (e) {
                                  toast({ title: 'Error unassigning lab', description: e.message, status: 'error', duration: 4000, isClosable: true });
                                } finally {
                                  setUnassignLoading(false);
                                }
                              }}>Unassign</Button>
                            </HStack>
                          </HStack>
                        </CardHeader>
                        <CardBody py={3}>
                          {selectedLabDetails ? (
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                              <Box>
                                <Text fontSize="xs" fontWeight="medium" color="gray.600">Name</Text>
                                <Text>{selectedLabDetails.name}</Text>
                              </Box>
                              <Box>
                                <Text fontSize="xs" fontWeight="medium" color="gray.600">Contact</Text>
                                <Text>{selectedLabDetails.contact_email || '—'}</Text>
                                <Text>{selectedLabDetails.contact_phone || ''}</Text>
                              </Box>
                              <Box gridColumn={{ base: '1', md: '1 / -1' }}>
                                <Text fontSize="xs" fontWeight="medium" color="gray.600">Address</Text>
                                <Text>
                                  {selectedLabDetails.address || ''}
                                  {selectedLabDetails.city ? `, ${selectedLabDetails.city}` : ''}
                                  {selectedLabDetails.state ? `, ${selectedLabDetails.state}` : ''}
                                  {selectedLabDetails.zip_code ? ` ${selectedLabDetails.zip_code}` : ''}
                                </Text>
                              </Box>
                            </SimpleGrid>
                          ) : (
                            <Text color="gray.600">Loading assigned lab details…</Text>
                          )}
                        </CardBody>
                      </Card>
                    )}
                    {/* Removed UUID manual assignment per request */}

                    {/* Search and assign by Lab Name */}
                    <FormControl>
                      <FormLabel fontSize="sm">Search lab by name (type 3+ characters)</FormLabel>
                      <Box position="relative">
                        <Input
                          placeholder="Search by lab name, city, or state"
                          value={labSearchQuery}
                          onChange={async (e) => {
                            const q = e.target.value;
                            setLabSearchQuery(q);
                            if (q && q.trim().length >= 3) {
                              setLabSearchLoading(true);
                              setShowLabDropdown(true);
                              try {
                                const { data, error } = await supabase
                                  .from('labs')
                                  .select('id, name, address, city, state, zip_code, contact_email, contact_phone')
                                  .or(`name.ilike.%${q}%,city.ilike.%${q}%,state.ilike.%${q}%`)
                                  .order('name')
                                  .limit(20);
                                if (!error) setLabSearchResults(data || []);
                              } finally {
                                setLabSearchLoading(false);
                              }
                            } else {
                              setShowLabDropdown(false);
                              setLabSearchResults([]);
                            }
                          }}
                          onFocus={() => { if (labSearchResults.length > 0) setShowLabDropdown(true); }}
                          onBlur={() => setTimeout(() => setShowLabDropdown(false), 300)}
                        />
                        {labSearchLoading && (
                          <Box position="absolute" right="10px" top="50%" transform="translateY(-50%)">
                            <Spinner size="sm" />
                          </Box>
                        )}
                        {showLabDropdown && (
                          <Box position="absolute" zIndex={10} w="100%" maxH="260px" overflowY="auto" bg="white" border="1px solid" borderColor="gray.300" borderRadius="md" mt={1} boxShadow="md" onMouseDown={(e) => e.preventDefault()}>
                            {labSearchResults.length === 0 ? (
                              <Box p={3}><Text fontSize="sm" color="gray.500">No labs found</Text></Box>
                            ) : (
                              labSearchResults.map((lab) => (
                                <HStack key={lab.id} p={2} borderBottomWidth={1} _hover={{ bg: 'gray.50' }} justify="space-between">
                                  <Box>
                                    <Text fontWeight="medium">{lab.name}</Text>
                                    <Text fontSize="xs" color="gray.600">{lab.city}, {lab.state} {lab.zip_code || ''}</Text>
                                  </Box>
                                  <Button size="sm" onMouseDown={(e) => e.preventDefault()} onClick={async () => {
                                    // Add to available list as selectable option (do not assign yet)
                                    // Add to a separate manual list so it isn't overwritten by async fetches
                                    // If it's already in the fetched list, just highlight it
                                    if (availableLabs.some(p => p.lab?.id === lab.id)) {
                                      setHighlightLabId(lab.id);
                                      setTimeout(() => setHighlightLabId(null), 1200);
                                      toast({ title: 'Already listed', description: 'Scrolled to existing option', status: 'info', duration: 1200, isClosable: true });
                                      return;
                                    }
                                    let nextList = [];
                                    setManualAddedLabs(prev => {
                                      if (prev.some(p => p.lab?.id === lab.id)) return prev;
                                      const option = {
                                        lab: {
                                          id: lab.id,
                                          name: lab.name,
                                          address: lab.address,
                                          city: lab.city,
                                          state: lab.state,
                                          zip_code: lab.zip_code,
                                          contact_email: lab.contact_email,
                                          contact_phone: lab.contact_phone,
                                        },
                                        tests: [],
                                        totalPrice: 0,
                                        pricedTestsCount: 0,
                                        turnaroundTime: 'Manual',
                                      };
                                      const next = [option, ...prev];
                                      nextList = next;
                                      setHighlightLabId(lab.id);
                                      setTimeout(() => setHighlightLabId(null), 1200);
                                      return next;
                                    });
                                    // Persist manual options on the submission
                                    try {
                                      const { error } = await supabase
                                        .from('submissions')
                                        .update({ manual_lab_options: nextList })
                                        .eq('id', submission.id);
                                      if (error) throw error;
                                    } catch (e) {
                                      console.warn('Failed to persist manual lab options:', e.message);
                                    }
                                    toast({ title: 'Added to options', description: lab.name, status: 'success', duration: 1500, isClosable: true });
                                    setShowLabDropdown(false);
                                    setLabSearchQuery('');
                                  }}>Add to list</Button>
                                </HStack>
                              ))
                            )}
                          </Box>
                        )}
                      </Box>
                    </FormControl>
                  </VStack>
                  {!(submission.lab_id || submission.ldx_message_id) && (
                   (availableLabsLoading && manualAddedLabs.length === 0) ? (
                    <Box textAlign="center" py={8}>
                      <Spinner size="xl" />
                      <Text mt={4}>Loading available labs...</Text>
                    </Box>
                  ) : (availableLabs.length === 0 && manualAddedLabs.length === 0) ? null : (
                    <VStack spacing={4} align="stretch">
                      <Text fontSize="xs" color="gray.500">Showing {manualAddedLabs.length + availableLabs.length} option(s) — added manually: {manualAddedLabs.length}, matched: {availableLabs.length}</Text>
                      {(() => {
                        // Combine manual-added labs with fetched labs, de-duplicated by lab.id
                        const combined = [...manualAddedLabs, ...availableLabs].filter((lab, index, arr) => {
                          const id = lab.lab?.id;
                          return id ? arr.findIndex(l => l.lab?.id === id) === index : true;
                        });
                        return combined.map((labData, index) => (
                        <Card key={index} shadow="md" borderWidth={submission.lab_id === labData.lab.id || highlightLabId === labData.lab.id ? 2 : 1} borderColor={(submission.lab_id === labData.lab.id) ? 'green.300' : (highlightLabId === labData.lab.id ? 'blue.300' : 'gray.200')}>
                          <CardHeader bg={submission.lab_id === labData.lab.id ? 'green.50' : (highlightLabId === labData.lab.id ? 'blue.50' : 'purple.50')}>
                            <HStack justify="space-between">
                              <Heading size="md" color={submission.lab_id === labData.lab.id ? 'green.700' : 'purple.700'}>{labData.lab.name}</Heading>
                              <HStack>
                                {submission.lab_id === labData.lab.id && (
                                  <Badge colorScheme="green" px={3} py={1}>Selected</Badge>
                                )}
                                <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                                  ${labData.totalPrice.toFixed(2)} Total
                                </Badge>
                              </HStack>
                            </HStack>
                          </CardHeader>
                          <CardBody py={3}>
                            <VStack spacing={3} align="stretch">
                              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                <Box>
                                  <Text fontSize="xs" fontWeight="medium" color="gray.600">Address</Text>
                                  <Text>{labData.lab.address}</Text>
                                  <Text>{labData.lab.city}, {labData.lab.state} {labData.lab.zip_code}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="xs" fontWeight="medium" color="gray.600">Contact</Text>
                                  {labData.lab.contact_phone && (
                                    <Text>Phone: {labData.lab.contact_phone}</Text>
                                  )}
                                  {labData.lab.contact_email && (
                                    <Text>Email: {labData.lab.contact_email}</Text>
                                  )}
                                </Box>
                              </SimpleGrid>

                              <Box pt={3} borderTopWidth={1}>
                                <Text fontSize="sm" fontWeight="bold" mb={2}>Tests Offered:</Text>
                                <Table variant="simple" size="sm">
                                  <Thead>
                                    <Tr>
                                      <Th>Test Name</Th>
                                      <Th>Price</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {labData.tests.map((test) => (
                                      <Tr key={test.id}>
                                        <Td fontWeight="medium">{test.name || test.id}</Td>
                                        <Td>
                                          <Text color={test.lab_price != null ? "green.600" : "gray.400"} fontWeight="semibold">
                                            {test.lab_price != null ? `$${Number(test.lab_price).toFixed(2)}` : 'Not set'}
                                          </Text>
                                        </Td>
                                      </Tr>
                                    ))}
                                  </Tbody>
                                </Table>
                              </Box>
                              <HStack spacing={2}>
                                <Button
                                  colorScheme="purple"
                                  isDisabled={submission.lab_id === labData.lab.id}
                                  onClick={async () => {
                                    try {
                                      const { error } = await supabase
                                        .from('submissions')
                                        .update({ lab_id: labData.lab.id })
                                        .eq('id', submission.id);
                                      if (error) throw error;
                                      setSubmission(prev => ({ ...prev, lab_id: labData.lab.id }));
                                      setSelectedLabDetails({
                                        id: labData.lab.id,
                                        name: labData.lab.name,
                                        address: labData.lab.address,
                                        city: labData.lab.city,
                                        state: labData.lab.state,
                                        zip_code: labData.lab.zip_code,
                                        contact_email: labData.lab.contact_email,
                                        contact_phone: labData.lab.contact_phone,
                                      });
                                      // Ensure the selected lab remains in manual list for phleb visibility
                                      setManualAddedLabs(prev => {
                                        if (prev.some(p => p.lab?.id === labData.lab.id)) return prev;
                                        return [{ lab: labData.lab, tests: labData.tests || [], totalPrice: labData.totalPrice || 0, pricedTestsCount: labData.pricedTestsCount || 0, turnaroundTime: labData.turnaroundTime || 'Manual' }, ...prev];
                                      });
                                      try {
                                        const persisted = [{ lab: labData.lab, tests: labData.tests || [], totalPrice: labData.totalPrice || 0, pricedTestsCount: labData.pricedTestsCount || 0, turnaroundTime: labData.turnaroundTime || 'Manual' }, ...manualAddedLabs];
                                        await supabase.from('submissions').update({ manual_lab_options: persisted }).eq('id', submission.id);
                                      } catch {}
                                      toast({ title: 'Lab assigned', description: labData.lab.name, status: 'success', duration: 2000, isClosable: true });
                                    } catch (e) {
                                      toast({ title: 'Error assigning lab', description: e.message, status: 'error', duration: 4000, isClosable: true });
                                    }
                                  }}
                                >
                                  {submission.lab_id === labData.lab.id ? 'Selected' : 'Assign this lab'}
                                </Button>
                                {submission.lab_id === labData.lab.id && (
                                  <Button
                                    colorScheme="red"
                                    variant="outline"
                                    onClick={async () => {
                                      try {
                                        const { error } = await supabase
                                          .from('submissions')
                                          .update({ lab_id: null })
                                          .eq('id', submission.id);
                                        if (error) throw error;
                                        setSubmission(prev => ({ ...prev, lab_id: null }));
                                        setSelectedLabDetails(null);
                                        toast({ title: 'Lab removed', description: 'Assignment cleared.', status: 'success', duration: 2000, isClosable: true });
                                      } catch (e) {
                                        toast({ title: 'Error removing lab', description: e.message, status: 'error', duration: 4000, isClosable: true });
                                      }
                                    }}
                                  >
                                    Remove
                                  </Button>
                                )}
                              </HStack>
                            </VStack>
                          </CardBody>
                        </Card>
                        ));
                      })()}
                    </VStack>
                  ))}

                {/* FedEx replaced by the Drop-off / Pickup section below.
                    Hidden by default; set REACT_APP_SHOW_FEDEX=true to restore. */}
                {SHOW_FEDEX_SECTION && (
                  <VStack spacing={6} align="stretch">
                    {/* FedEx Label Card */}
                    <Card variant="outline" borderColor="gray.200" borderRadius="lg" shadow="xs">
                      <CardHeader bg="orange.50" py={1.5}>
                        <HStack justify="space-between">
                          <Heading size="sm" color="orange.700">FedEx Shipping Label</Heading>
                          {!submission.fedex_label_url && (
                            <Button
                              colorScheme="orange"
                              size="sm"
                              leftIcon={<AttachmentIcon />}
                              onClick={handleFedexModalOpen}
                            >
                              Generate Label
                            </Button>
                          )}
                        </HStack>
                      </CardHeader>
                      <CardBody py={3}>
                        {submission.fedex_label_url ? (
                          <VStack spacing={4} align="stretch">
                            <Box>
                              <Text fontWeight="bold" mb={2}>Tracking Number:</Text>
                              <Text fontSize="lg" fontFamily="mono">{submission.fedex_tracking_number || 'N/A'}</Text>
                            </Box>
                            <Link href={submission.fedex_label_url} isExternal>
                              <Button colorScheme="blue" leftIcon={<AttachmentIcon />}>
                                View/Download FedEx Label
                              </Button>
                            </Link>
                            {submission.fedex_tracking_number && (
                              <Button
                                colorScheme="purple"
                                variant="outline"
                                onClick={handleTrackShipment}
                                isLoading={trackingLoading}
                              >
                                Track Shipment
                              </Button>
                            )}
                          </VStack>
                        ) : (
                          <VStack align="stretch" spacing={4}>
                            <Alert status="info">
                              <AlertIcon />
                              <Box>
                                <AlertTitle>No FedEx label yet</AlertTitle>
                                <AlertDescription>
                                  Generate a label, or provide one manually below.
                                </AlertDescription>
                              </Box>
                            </Alert>
                            <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                              <FormControl>
                                <FormLabel>Paste Label URL (optional)</FormLabel>
                                <Input value={manualLabelUrl} onChange={(e) => setManualLabelUrl(e.target.value)} placeholder="https://..." />
                              </FormControl>
                              <FormControl>
                                <FormLabel>Upload Label File (PDF/PNG)</FormLabel>
                                <Input type="file" accept="application/pdf,image/*" onChange={(e) => setManualLabelFile(e.target.files?.[0] || null)} />
                              </FormControl>
                              <FormControl>
                                <FormLabel>Tracking Number</FormLabel>
                                <Input value={manualTrackingNumber} onChange={(e) => setManualTrackingNumber(e.target.value)} placeholder="e.g. 123456789012" />
                              </FormControl>
                            </SimpleGrid>
                            <HStack>
                              <Button
                                colorScheme="green"
                                isLoading={manualLabelSaving}
                                onClick={async () => {
                                  try {
                                    if (!manualLabelUrl && !manualLabelFile) {
                                      toast({ title: 'Provide a label', description: 'Paste a URL or upload a file.', status: 'warning', duration: 3000, isClosable: true });
                                      return;
                                    }
                                    setManualLabelSaving(true);
                                    let finalUrl = manualLabelUrl;
                                    if (manualLabelFile) {
                                      const ext = (manualLabelFile.name.split('.').pop() || 'pdf').toLowerCase();
                                      const path = `manual-fedex/${submission.id}_${Date.now()}.${ext}`;
                                      const { error: upErr } = await supabase.storage.from('fedex-labels').upload(path, manualLabelFile, { upsert: true });
                                      if (upErr) throw upErr;
                                      const { data: urlData } = await supabase.storage.from('fedex-labels').getPublicUrl(path);
                                      finalUrl = urlData.publicUrl;
                                    }
                                    const update = { fedex_label_url: finalUrl, fedex_tracking_number: manualTrackingNumber || null, status: 'completed' };
                                    const { error } = await supabase.from('submissions').update(update).eq('id', submission.id);
                                    if (error) throw error;
                                    setSubmission(prev => ({ ...prev, ...update }));
                                    toast({ title: 'Label recorded', description: 'Manual FedEx label saved and shipment marked inbound.', status: 'success', duration: 3000, isClosable: true });
                                  } catch (e) {
                                    toast({ title: 'Failed to save label', description: e.message, status: 'error', duration: 4000, isClosable: true });
                                  } finally {
                                    setManualLabelSaving(false);
                                  }
                                }}
                              >
                                Mark Label Provided
                              </Button>
                            </HStack>
                          </VStack>
                        )}
                      </CardBody>
                    </Card>

                    {/* Tracking Information */}
                    {trackingInfo && (
                      <Card variant="outline" borderColor="gray.200" borderRadius="lg" shadow="xs">
                        <CardHeader bg="purple.50" py={1.5}>
                          <Heading size="sm" color="purple.700">Tracking Information</Heading>
                        </CardHeader>
                        <CardBody py={3}>
                          <VStack spacing={4} align="stretch">
                            <Box>
                              <Text fontWeight="bold" mb={2}>Status:</Text>
                              <Badge colorScheme={trackingInfo.status?.toLowerCase() === 'delivered' ? 'green' : 'blue'} fontSize="md">
                                {trackingInfo.status || 'In Transit'}
                              </Badge>
                            </Box>
                            {trackingInfo.destination && (
                              <Box>
                                <Text fontWeight="bold" mb={2}>Destination:</Text>
                                <Text>{trackingInfo.destination}</Text>
                              </Box>
                            )}
                            {trackingInfo.estimatedDelivery && (
                              <Box>
                                <Text fontWeight="bold" mb={2}>Estimated Delivery:</Text>
                                <Text>{new Date(trackingInfo.estimatedDelivery).toLocaleString()}</Text>
                              </Box>
                            )}
                          </VStack>
                        </CardBody>
                      </Card>
                    )}
                  </VStack>
                )}

                {/* Results Tab */}
                  <Card variant="outline" borderColor="gray.200" borderRadius="lg" shadow="xs">
                    <CardHeader bg="green.50" py={1.5}>
                      <Heading size="sm" color="green.700">Lab Results</Heading>
                    </CardHeader>
                    <CardBody py={3}>
                      <VStack align="stretch" spacing={3}>
                        {submission.lab_results_url ? (
                          <>
                            <HStack justify="space-between">
                              <Link href={submission.lab_results_url} isExternal color="blue.500">
                                View Lab Results
                              </Link>
                              <Button
                                size="sm"
                                colorScheme="red"
                                variant="ghost"
                                onClick={async () => {
                                  if (!window.confirm('Remove lab results? The submission stays Completed, without results.')) return;
                                  try {
                                    const { error } = await supabase
                                      .from('submissions')
                                      .update({ lab_results_url: null, status: 'completed' })
                                      .eq('id', submission.id);
                                    if (error) throw error;
                                    setSubmission(prev => ({ ...prev, lab_results_url: null, status: 'completed' }));
                                    toast({ title: 'Lab results removed', status: 'success', duration: 3000, isClosable: true });
                                  } catch (err) {
                                    toast({ title: 'Failed to remove results', description: err.message, status: 'error', duration: 4000, isClosable: true });
                                  }
                                }}
                              >
                                Remove Result
                              </Button>
                            </HStack>
                            <Text fontSize="sm" color="gray.500">Replace existing file:</Text>
                          </>
                        ) : (
                          <Text color="gray.500">No lab results uploaded</Text>
                        )}
                        <HStack>
                          <Input
                            type="file"
                            accept="application/pdf,image/*"
                            display="none"
                            id="lab-results-upload"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              try {
                                setLabResultsUploading(true);
                                const fileExt = file.name.split('.').pop() || 'pdf';
                                const filePath = `lab-results/${submission.id}_${Date.now()}.${fileExt}`;
                                const { error: uploadError } = await supabase.storage.from('lab-results').upload(filePath, file, { upsert: true });
                                if (uploadError) throw uploadError;
                                const { data: urlData } = supabase.storage.from('lab-results').getPublicUrl(filePath);
                                const resultsUrl = urlData.publicUrl;
                                const updateData = { lab_results_url: resultsUrl };
                                if (submission.status !== 'completed') {
                                  updateData.status = 'completed';
                                }
                                const { error } = await supabase.from('submissions').update(updateData).eq('id', submission.id);
                                if (error) throw error;
                                setSubmission(prev => ({ ...prev, lab_results_url: resultsUrl, ...updateData }));
                                toast({ title: 'Lab results uploaded', description: submission.status !== 'completed' ? 'Submission marked as completed.' : '', status: 'success', duration: 3000, isClosable: true });
                                // Notify the doctor via email
                                if (submission.doctor_email) {
                                  const apiBase = process.env.REACT_APP_API_URL || '';
                                  fetch(`${apiBase}/api/notify-doctor-results`, {
                                    method: 'POST',
                                    headers: getAuthHeaders(),
                                    body: JSON.stringify({
                                      submissionId: submission.id,
                                      doctorEmail: submission.doctor_email,
                                      doctorName: submission.doctor_name,
                                      patientName: submission.patient_name,
                                      resultsUrl,
                                    }),
                                  }).catch(err => console.error('Failed to notify doctor:', err));
                                }
                              } catch (err) {
                                toast({ title: 'Upload failed', description: err.message, status: 'error', duration: 4000, isClosable: true });
                              } finally {
                                setLabResultsUploading(false);
                                e.target.value = '';
                              }
                            }}
                          />
                          <Button
                            as="label"
                            htmlFor="lab-results-upload"
                            colorScheme="green"
                            leftIcon={<AttachmentIcon />}
                            cursor="pointer"
                            isLoading={labResultsUploading}
                            loadingText="Uploading..."
                            isDisabled={labResultsUploading}
                          >
                            Upload File
                          </Button>
                        </HStack>
                      </VStack>
                    </CardBody>
                  </Card>

              </VStack>
            </Flex>
          </Box>
        </Flex>
      </Box>

      {/* FedEx Label Generator Modal */}
      <FedExLabelGenerator
        submissionId={submission?.id}
        isOpen={fedexModalOpen}
        onClose={handleFedexModalClose}
        onLabelGenerated={handleLabelGenerated}
      />

      {/* Status updater — same drawer the phleb uses, available to admin too */}
      <OutcomeDrawer
        isOpen={statusDrawerOpen}
        onClose={() => setStatusDrawerOpen(false)}
        submission={submission}
        phlebotomistId={submission?.phlebotomist_id || null}
        // Stamp the actual admin who clicked, so the activity timeline
        // shows their name instead of a generic "Admin" badge. Falls back
        // to legacy phleb attribution if adminUser isn't loaded yet
        // (e.g. opening the page in an unauthenticated context).
        loggedByKind={adminUser?.id ? 'admin' : undefined}
        loggedById={adminUser?.id || undefined}
        loggedByName={adminUser?.name || adminUser?.email || undefined}
        onLogged={(updatedFields) => {
          setSubmission((prev) => (prev ? { ...prev, ...updatedFields } : prev));
          if (updatedFields?.id) fetchStatusEvents(updatedFields.id);
        }}
      />

      <MergeCandidatePicker
        isOpen={mergePickerOpen}
        onClose={() => setMergePickerOpen(false)}
        submission={submission}
        onPick={onPickMergeDuplicate}
      />

      {mergePair && (
        <MergeSubmissionsModal
          isOpen={!!mergePair}
          onClose={() => setMergePair(null)}
          primary={mergePair.primary}
          duplicate={mergePair.duplicate}
          onMerged={({ keptId }) => { setMergePair(null); window.location.assign(`/admin/submissions/${keptId}`); }}
        />
      )}
    </Box>
  );
}

export default SubmissionDetailPage;
